import { appendSignatureHtml, plainTextToEmailHtml } from '../../lib/emailHtml';
import { withComms292Fallback } from '../../lib/commsSchemaFallback';
import { enqueueBulk, BULK_QUEUE_THRESHOLD } from '../../lib/commsBulk';
import { capabilityPhone, checkPhoneLine } from '../../lib/phoneChannelApi';
import { resolveMainSmsLine } from '../../lib/mainSmsLine';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { cleanEntries, entriesOf, primaryEntry, saveContactMethods } from '../../lib/contactMethods';
import { useSupportSession } from '../../context/SupportSessionContext';
import IntakeSessionDetailCard from './IntakeSessionDetailCard';
import CommunicationsThreadList from './CommunicationsThreadList';
import EmailMailboxWorkspace from './EmailMailboxWorkspace';
import ComposeNewMessagePanel from './ComposeNewMessagePanel';
import StaffReservation from '../reservations/StaffReservation';
import CommunicationsSettingsModal from './CommunicationsSettingsModal';
import {
    buildContactSearchIndexes,
    buildThreadList,
    collectStaffUserIds,
    resolveStaffDisplayName,
} from '../../lib/communicationsThread';
import { buildFlagPatch, buildManualReopenPatch } from '../../lib/communicationsFlags';
import {
    appendInternalNotes,
    buildInternalNotesReadBy,
    buildManualInternalNote,
    buildStatusTimelineEntries,
} from '../../lib/communicationsNotes';
import {
    buildAccountUpdate,
    buildContactUpdate,
    buildNewAccountInsert,
    buildNewContactInsert,
    buildOutboundSessionInsert,
    buildRecipientFanout,
    findActiveThreadForContact,
    getContactPhoneNumber,
    normalizeOutboundPhoneAddress,
} from '../../lib/communicationsCompose';
import { sendStaffReply } from '../../lib/staffReply';
import { buildTemplateVariableValues, fillTemplateBody } from '../../lib/communicationsTemplates';
import { cancelScheduledMessage, scheduleStaffReply } from '../../lib/scheduledSendApi';
import { useIsCompanyAdmin, useScheduledMessages } from './useScheduledMessages';
import { formatScheduledWhen } from '../../lib/scheduledSend';
import { signOutgoingBody } from './useMessageSignature';
import { ensureEmailConversationFolder } from '../../lib/emailConversationFolder';
import {
    buildCommunicationsCssVars,
    buildCommunicationsSettingsUpsert,
    COMMUNICATIONS_SETTINGS_SELECT,
    COMMUNICATIONS_SETTINGS_SELECT_BEFORE_281,
    DEFAULT_COMMUNICATIONS_SETTINGS,
    isMissingMigration281ColumnError,
    withoutMigration281Columns,
    DEFAULT_PREVIEW_LINES,
    normalizeCommunicationsSettings,
    readPreviewLines,
    writePreviewLines,
} from '../../lib/communicationsSettings';
import {
    DEFAULT_ENTER_KEY,
    EnterKeyPreferenceContext,
    loadEnterKeyPreference,
} from '../../lib/enterKeyPreference';
import {
    closeGroupConversation,
    groupState,
    inviteColleague,
    isHostOf,
    leaveConversation,
    listInvitable,
    listParticipants,
    passTo,
    pendingInviteFor,
    requestToJoin,
    splitByInbox,
    takeConversation,
    withdrawRequest,
} from '../../lib/conversationAccess';
import useConversationAccess, { announceAccessChanged } from './useConversationAccess';
import ConversationAccessModal from './ConversationAccessModal';
import ColleagueConversations from './ColleagueConversations';
import {
    AI_ASSISTANT_SETTINGS_COLUMNS,
    describeAiAssistantStatus,
    DEFAULT_AI_ASSISTANT_SETTINGS,
} from '../../lib/aiAssistantGate';
import { describePause } from '../../lib/businessSchedule';
import { groupParticipants } from '../../lib/communicationRecipients';
import { sendNewGroupText } from '../../lib/groupSend';
import { participantNameMap } from '../../lib/communicationsDisplay';
import { RETIRED_SMS_LINE } from '../../lib/smsSenderSelection';
import { fetchNamingContacts, fetchPersonalContactsStatus, findMyContactForSession } from '../../lib/personalContacts';
import { announceConversationEngaged } from '../../lib/notificationInbox';
import { TONES } from '../../lib/colorMeaning';
import StatusChip from '../ui/StatusChip';
import AppLink from '../ui/AppLink';
import useCommsViewport, { useVisualViewportVars } from '../../hooks/useCommsViewport';
import TeamsTextMirror from '../integrations/teams/TeamsTextMirror';
import { canUseIntegration, INTEGRATION_KEYS, useIntegrationAccess } from '../../lib/integrations/integrationGate';
import { loadVoicemailState, saveVoicemailState } from '../../lib/voicemailState';
import { clearTemplateSettingsReturn, readTemplateSettingsReturn } from '../../lib/templateSettingsReturn';
import {
    DEFAULT_GATE_STATUS, loadGateStatus, loadGateTimeline, loadGroupSettings, openNoticeText, releaseConversation,
} from '../../lib/conversationGate';
import { brandNameForEntity, setTemplateRuntimeDefaults } from '../../lib/templateFieldResolver';
import { OPEN_CONVERSATION_EVENT, sessionIdFromSearch } from '../../lib/conversationLinks';
import { loadUnseenMentionSessions, markMentionsSeen, noteIdFromSearch } from '../../lib/noteMentions';

// BL-241 Gate 2 — two-pane Communications panel. Left: every intake session for the org, newest
// activity first, searchable. Right: the selected thread as chat bubbles, with the reply composer
// at the bottom and the raw logs behind "View technical log ↗". Read-only restructure: no Edge
// Function, log write path, or AI Attendant behaviour is touched here.
//
// BL-241 Gate 3 adds one write path and only one: the manual flag/status icon on a thread row
// updates the intake_sessions flag_* / resolved_* columns through the same Supabase client. No
// automated flag-setting is wired here — that stays with the AI Attendant, elsewhere.

const SESSION_COLUMNS = 'id, org_id, channel, external_thread_id, email_provider_id, email_subject, account_id, confirmation_number, contact_phone, contact_email, client_tier, itinerary_short_form, pass_count, pass_cap, approval_status, outcome, last_inbound_at, last_outbound_at, created_at, updated_at, capture_checklist, conversation_log, raw_message_log, flag_status, flag_urgency, flag_active, flagged_at, flagged_by, flag_source, resolved_at, resolved_by, internal_notes, internal_notes_read_by, ai_paused_until, ai_paused_by, ai_pause_reason, archived_at, archived_by, requires_human_review';
const CONTACT_COLUMNS = 'id, org_id, name, first_name, last_name, emails, phones, primary_account_id, entity_id, requires_human_review, sms_opted_out_at, sms_transactional_consent, sms_marketing_consent, sms_consent_source, sms_consent_at, sms_consent_text, sms_consent_by, sms_customer_agreed_verbally, sms_consent_note, communication_preferences, communication_preferences_set_by, communication_preferences_set_at, email_opted_out_at';
const ACCOUNT_COLUMNS = 'id, account_name, is_business, primary_contact_id, entity_id';
const ENTITY_COLUMNS = 'id, org_id, name, display_label, parent_entity_id, is_booking_node, brand_name, status';
const THREAD_LIST_DEFAULT_WIDTH = 320;
const EMPTY_LIST = Object.freeze([]);
const THREAD_LIST_WIDE_DEFAULT_WIDTH = 560;
const THREAD_LIST_MIN_WIDTH = 240;
const THREAD_LIST_DESKTOP_MIN_WIDTH = 552;
const THREAD_LIST_MAX_WIDTH = 560;

function threadListWidthBounds(containerWidth = 0) {
    const responsiveMax = containerWidth ? Math.min(THREAD_LIST_MAX_WIDTH, Math.floor(containerWidth * 0.45)) : THREAD_LIST_MAX_WIDTH;
    const wideDesktop = typeof window !== 'undefined' && window.innerWidth >= 1280 && containerWidth >= 960;
    const minWidth = wideDesktop ? THREAD_LIST_DESKTOP_MIN_WIDTH : THREAD_LIST_MIN_WIDTH;
    const maxWidth = Math.max(minWidth, responsiveMax);
    return { minWidth, maxWidth };
}

function clampThreadListWidth(width, containerWidth = 0) {
    const { minWidth, maxWidth } = threadListWidthBounds(containerWidth);
    return Math.min(maxWidth, Math.max(minWidth, width));
}

function getInitialThreadListWidth() {
    return typeof window !== 'undefined' && window.innerWidth >= 1280
        ? THREAD_LIST_WIDE_DEFAULT_WIDTH
        : THREAD_LIST_DEFAULT_WIDTH;
}

function normalizePhoneDigits(value) {
    return normalizeOutboundPhoneAddress(value).replace(/\D/g, '');
}

function normalizeEmail(value) {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

// BL-177 Step 2m: the header pill reads the company's AI status (Off / On / Pause / Test, and whether "On" means after hours only).
// The page that changes it is Business schedule > AI integration. No saved status (or a settings row that failed to load) reads as Off.
const AI_INTEGRATION_PATH = '/admin/business-schedule#ai-integration';

export { describeAiAssistantStatus } from '../../lib/aiAssistantGate';

function findContactForSession(session, contacts = [], account = null) {
    if (!session) return null;
    const orgContacts = session.org_id
        ? contacts.filter((contact) => contact?.org_id === session.org_id)
        : contacts;
    if (account?.primary_contact_id) {
        const primary = orgContacts.find((contact) => contact.id === account.primary_contact_id);
        if (primary) return primary;
    }

    if (session.account_id) {
        const accountContact = orgContacts.find((contact) => contact.primary_account_id === session.account_id);
        if (accountContact) return accountContact;
    }
    const sessionPhone = normalizePhoneDigits(session.contact_phone);
    const sessionEmail = normalizeEmail(session.contact_email);
    return orgContacts.find((contact) => {
        const phones = Array.isArray(contact?.phones) ? contact.phones : [];
        const emails = Array.isArray(contact?.emails) ? contact.emails : [];
        return Boolean(
            // a phone entry may be a plain string or { number, whatsapp } (final pass: object entries never matched before)
            (sessionPhone && phones.some((phone) => normalizePhoneDigits(getContactPhoneNumber(phone) || phone) === sessionPhone))
            || (sessionEmail && emails.some((email) => normalizeEmail(email) === sessionEmail))
        );
    }) || null;
}

// BL-177 Step 2d (section 2): flags and bubbles use the shared colour system on every Communications screen. These
// replace the org's custom flag/bubble colours (still stored in Communications settings, no longer painted).
const STEP2D_COMMS_VARS = Object.freeze({
    '--comms-flag-followup': TONES.blue.border,
    '--comms-flag-followthru': TONES.red.border,
    '--comms-flag-urgent': TONES.red.border,
    '--comms-flag-asap': TONES.red.border,
    '--comms-flag-needs-attention': TONES.orange.border,
    '--comms-flag-in-progress': TONES.yellow.border,
    '--comms-flag-resolved': TONES.green.border,
    '--comms-flag-closed': TONES.grey.border,
    '--comms-bubble-inbound': TONES.slate.bg,
});

// Teams PR B: `?teamsThread=<id>&teamsKind=team|client` opens the Team chat tab or the client Teams view.
export function teamsTabFromSearch(search) {
    const params = new URLSearchParams(search || '');
    if (!params.has('teamsThread')) return '';
    return params.get('teamsKind') === 'team' ? 'teams' : 'teams-client';
}

function CommunicationsView({ theme: appTheme = 'light', orgId = null, platformDoorContext = null, headerBrandEntityId = '' }) {
    const theme = appTheme;
    const dark = theme === 'dark';
    const { effectiveOrgId } = useSupportSession();
    const resolvedOrgId = orgId || effectiveOrgId || null;
    const [storedSessions, setSessions] = useState([]);
    // Event guests are not in CRM, so their conversations are named from the guest list. Filled in below; a failed lookup just shows the number.
    const [eventGuestLabels, setEventGuestLabels] = useState({});
    const sessions = useMemo(() => storedSessions.map((session) => (
        eventGuestLabels[session.id] ? { ...session, event_guest: eventGuestLabels[session.id] } : session
    )), [eventGuestLabels, storedSessions]);
    const [accounts, setAccounts] = useState([]);
    const [contacts, setContacts] = useState([]);
    // BL-177 PR D: My Contacts that may name a conversation for the signed-in person (own first, then shared with them). Empty when the feature is off.
    const [myContacts, setMyContacts] = useState([]);
    const [profileContact, setProfileContact] = useState(null);
    const [entities, setEntities] = useState([]);
    const [staffNames, setStaffNames] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    // Comms next pass: coming back from Settings → Templates ("Edit templates"): reopen the same conversation (or the
    // New message form) with the unsent draft. Read once; cleared after mount so a later visit starts clean.
    const [templateReturnNote] = useState(() => readTemplateSettingsReturn());
    const [selectedSessionId, setSelectedSessionId] = useState(() => (
        sessionIdFromSearch(globalThis.location?.search)
        || (templateReturnNote?.kind === 'reply' ? templateReturnNote.sessionId : null)
    ));
    const [flagError, setFlagError] = useState(null);
    const [contactError, setContactError] = useState(null);
    const [composeOpen, setComposeOpen] = useState(() => templateReturnNote?.kind === 'compose');
    const [contactSearch, setContactSearch] = useState('');
    const [composeError, setComposeError] = useState('');
    const [threadContactError, setThreadContactError] = useState('');
    const [savingThreadContact, setSavingThreadContact] = useState(false);
    const [deletingThread, setDeletingThread] = useState(false);
    const [startingThread, setStartingThread] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [voicemailStateByKey, setVoicemailStateByKey] = useState({});
    const [voicemailStateError, setVoicemailStateError] = useState('');
    const [voicemailStateLoading, setVoicemailStateLoading] = useState(false);
    const teamsAccess = useIntegrationAccess(INTEGRATION_KEYS.MICROSOFT_TEAMS, currentUserId);
    const teamsEnabled = canUseIntegration(teamsAccess);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [communicationsSettings, setCommunicationsSettings] = useState(DEFAULT_COMMUNICATIONS_SETTINGS);
    const [settingsError, setSettingsError] = useState('');
    // BL-177 Step 2 Part B: this person's "When I press Enter" choice, saved on their profile so it
    // follows them across computer and phone.
    // The person's own choice (Settings > Communications > Typing & sending), read here so every composer honors it. Never edited from this screen.
    const [enterKeyMode, setEnterKeyMode] = useState(DEFAULT_ENTER_KEY);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [previewLines, setPreviewLines] = useState(DEFAULT_PREVIEW_LINES);
    const [aiAssistantSettings, setAiAssistantSettings] = useState(DEFAULT_AI_ASSISTANT_SETTINGS);
    const [replyDraftsBySession, setReplyDraftsBySession] = useState(() => (
        templateReturnNote?.kind === 'reply' && templateReturnNote.body.trim()
            ? { [templateReturnNote.sessionId]: { body: templateReturnNote.body, subject: templateReturnNote.subject } }
            : {}
    ));
    // The New message form reads its starting body once, when it mounts; after that the restore is spent.
    const [composeRestore, setComposeRestore] = useState(() => (templateReturnNote?.kind === 'compose' ? templateReturnNote : null));
    useEffect(() => {
        if (templateReturnNote) clearTemplateSettingsReturn();
    }, [templateReturnNote]);
    useEffect(() => {
        if (composeOpen && composeRestore) setComposeRestore(null);
    }, [composeOpen, composeRestore]);
    const [threadListWidth, setThreadListWidth] = useState(getInitialThreadListWidth);
    const [workspaceTab, setWorkspaceTab] = useState(() => teamsTabFromSearch(globalThis.location?.search) || 'conversations');
    const [teamsThreadId, setTeamsThreadId] = useState(() => new URLSearchParams(globalThis.location?.search || '').get('teamsThread'));
    // Teams PR B: a Teams alert opened from the bell (pushState + popstate) while Communications is already on screen.
    useEffect(() => {
        const onPop = () => {
            const tab = teamsTabFromSearch(window.location.search);
            if (!tab) return;
            setTeamsThreadId(new URLSearchParams(window.location.search).get('teamsThread'));
            setWorkspaceTab(tab);
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);
    const [sendingIdentities, setSendingIdentities] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [campaignError, setCampaignError] = useState('');
    const [isResizingThreadList, setIsResizingThreadList] = useState(false);
    const [threadListContainerWidth, setThreadListContainerWidth] = useState(0);
    const resizePointerRef = useRef(null);
    const resizeContainerRef = useRef(null);
    const resizeBounds = threadListWidthBounds(threadListContainerWidth);
    const resizeStartXRef = useRef(0);
    const resizeStartWidthRef = useRef(THREAD_LIST_DEFAULT_WIDTH);
    const defaultEntityId = entities[0]?.id || contacts.find((contact) => contact.entity_id)?.entity_id || null;

    useEffect(() => {
        let mounted = true;
        if (!resolvedOrgId) {
            setVoicemailStateByKey({});
            setVoicemailStateError('');
            setVoicemailStateLoading(false);
            return undefined;
        }
        setVoicemailStateLoading(true);
        setVoicemailStateError('');
        loadVoicemailState(supabaseClient, resolvedOrgId)
            .then((states) => {
                if (mounted) setVoicemailStateByKey(states);
            })
            .catch((loadError) => {
                if (mounted) setVoicemailStateError(loadError.message || 'Could not load voicemail state.');
            })
            .finally(() => {
                if (mounted) setVoicemailStateLoading(false);
            });
        return () => { mounted = false; };
    }, [resolvedOrgId]);

    const handleUpdateVoicemailState = useCallback(async ({ sessionId, callSid, changes }) => {
        try {
            const saved = await saveVoicemailState(supabaseClient, {
                orgId: resolvedOrgId,
                sessionId,
                callSid,
                changes,
            });
            setVoicemailStateByKey((current) => ({
                ...current,
                [`${sessionId}:${callSid}`]: saved,
            }));
            setVoicemailStateError('');
        } catch (saveError) {
            setVoicemailStateError(saveError.message || 'Could not update voicemail state.');
        }
    }, [resolvedOrgId]);

    const loadStaffNames = useCallback(async (loadedSessions) => {
        const ids = [
            ...new Set([
                ...collectStaffUserIds(loadedSessions),
                ...(currentUserId ? [currentUserId] : []),
            ]),
        ];
        if (ids.length === 0) return;
        try {
            // BL-177 Step 2c (F4): one display name per staff member (profile name, else the staff record's name).
            const { data, error: staffError } = await supabaseClient.rpc('bl177_staff_display_names', { p_user_ids: ids });
            if (staffError || !Array.isArray(data)) return;
            setStaffNames((current) => ({
                ...current,
                ...Object.fromEntries(data
                    .filter((row) => row?.display_name)
                    .map((row) => [row.user_id, row.display_name])),
            }));
        } catch {
            // Staff names are a nicety — an unresolved id simply renders as "Staff".
        }
    }, [currentUserId]);

    const loadSessions = useCallback(async () => {
        setError(null);
        // BL-177 Step 2d: intake_sessions.after_hours (Step 2d migration) drives the row's After hours tag. Until that
        // migration is applied the column doesn't exist, so the list falls back to today's columns instead of failing.
        const loadIntakeSessions = async () => {
            const query = (columns) => supabaseClient.from('intake_sessions')
                .select(columns)
                .order('last_inbound_at', { ascending: false, nullsFirst: false });
            const withAfterHours = await query(`${SESSION_COLUMNS}, after_hours`);
            if (withAfterHours?.error && /after_hours/i.test(String(withAfterHours.error.message || ''))) return query(SESSION_COLUMNS);
            return withAfterHours;
        };
        const [sessionsResult, accountsResult, contactsResult, entitiesResult, campaignsResult, recipientsResult] = await Promise.all([
            loadIntakeSessions(),
            supabaseClient.from('accounts').select(ACCOUNT_COLUMNS).order('account_name'),
            withComms292Fallback(columns => supabaseClient.from('contacts').select(columns).order('name'), CONTACT_COLUMNS),
            supabaseClient.from('entities').select(ENTITY_COLUMNS).order('name'),
            supabaseClient.from('comm_campaigns')
                .select('id, channel, created_at, started_at, recipient_count, skipped_count, status')
                .order('created_at', { ascending: false }),
            supabaseClient.from('comm_campaign_recipients')
                .select('id, campaign_id, contact_id, intake_session_id, destination, status, provider_error_message, skipped_reason, sent_at')
                .order('created_at'),
        ]);
        if (sessionsResult.error) {
            setError(sessionsResult.error.message);
            setSessions([]);
        } else {
            const loaded = sessionsResult.data || [];
            setSessions(loaded);
            loadStaffNames(loaded);
        }
        if (!accountsResult.error) setAccounts(accountsResult.data || []);
        if (contactsResult.error) {
            setContacts([]);
            setContactError(contactsResult.error.message);
        } else {
            setContacts(contactsResult.data || []);
            setContactError(null);
        }
        if (!entitiesResult.error) setEntities(entitiesResult.data || []);
        if (campaignsResult.error || recipientsResult.error) {
            setCampaignError(campaignsResult.error?.message || recipientsResult.error?.message || 'Could not load campaigns.');
            setCampaigns([]);
        } else {
            const sessionsById = new Map((sessionsResult.data || []).map((session) => [session.id, session]));
            const contactsById = new Map((contactsResult.data || []).map((contact) => [contact.id, contact]));
            const recipientsByCampaign = new Map();
            (recipientsResult.data || []).forEach((row) => {
                const session = sessionsById.get(row.intake_session_id);
                const sentAt = row.sent_at ? new Date(row.sent_at).getTime() : 0;
                const replied = (session?.raw_message_log || []).some((entry) => (
                    entry?.direction === 'inbound'
                    && new Date(entry.received_at || entry.at || 0).getTime() > sentAt
                ));
                const optedOut = row.skipped_reason === 'sms_opted_out' || row.status === 'opted_out';
                const displayStatus = optedOut
                    ? 'Opted out'
                    : (replied ? 'Replied' : (row.status === 'undelivered' ? 'Failed' : row.status));
                const recipient = {
                    ...row,
                    contactName: contactsById.get(row.contact_id)?.name || '',
                    replied,
                    optedOut,
                    displayStatus,
                };
                if (!recipientsByCampaign.has(row.campaign_id)) recipientsByCampaign.set(row.campaign_id, []);
                recipientsByCampaign.get(row.campaign_id).push(recipient);
            });
            setCampaigns((campaignsResult.data || []).map((campaign) => {
                const campaignRecipients = recipientsByCampaign.get(campaign.id) || [];
                const counts = {
                    delivered: campaignRecipients.filter((row) => ['delivered', 'read'].includes(row.status)).length,
                    replied: campaignRecipients.filter((row) => row.replied).length,
                    optedOut: campaignRecipients.filter((row) => row.optedOut).length,
                    skipped: campaignRecipients.filter((row) => row.status === 'skipped' && !row.optedOut).length,
                };
                const createdAt = new Date(campaign.created_at);
                const sentDate = Number.isNaN(createdAt.getTime()) ? 'Date unavailable' : createdAt.toLocaleString();
                return {
                    ...campaign,
                    displayName: `Campaign · ${campaign.channel} · ${sentDate}`,
                    sentDate,
                    recipientCount: campaign.recipient_count || campaignRecipients.length,
                    counts,
                    recipients: campaignRecipients,
                };
            }));
            setCampaignError('');
        }
        setLoading(false);
    }, [loadStaffNames]);

    const loadCommunicationsSettings = useCallback(async () => {
        if (!resolvedOrgId) {
            setCommunicationsSettings(DEFAULT_COMMUNICATIONS_SETTINGS);
            return;
        }
        try {
            const loadWith = (columns) => withComms292Fallback(selectColumns => supabaseClient
                .from('org_communications_settings')
                .select(selectColumns)
                .eq('org_id', resolvedOrgId)
                .maybeSingle(), columns);
            let { data, error: loadError } = await loadWith(COMMUNICATIONS_SETTINGS_SELECT);
            // Review fix round 1: the site may be live before migration 281 — load without its columns (defaults apply).
            if (isMissingMigration281ColumnError(loadError)) {
                ({ data, error: loadError } = await loadWith(COMMUNICATIONS_SETTINGS_SELECT_BEFORE_281));
            }
            if (loadError) {
                setSettingsError(loadError.message || 'Could not load Communications settings.');
                setCommunicationsSettings(DEFAULT_COMMUNICATIONS_SETTINGS);
                return;
            }
            setSettingsError('');
            setCommunicationsSettings(normalizeCommunicationsSettings(data || {}));
        } catch (loadError) {
            setSettingsError(loadError.message || 'Could not load Communications settings.');
            setCommunicationsSettings(DEFAULT_COMMUNICATIONS_SETTINGS);
        }
    }, [resolvedOrgId]);

    const loadAiAssistantSettings = useCallback(async () => {
        if (!resolvedOrgId) {
            setAiAssistantSettings(DEFAULT_AI_ASSISTANT_SETTINGS);
            return;
        }
        const { data, error: loadError } = await supabaseClient
            .from('org_ai_assistant_settings')
            .select(AI_ASSISTANT_SETTINGS_COLUMNS)
            .eq('org_id', resolvedOrgId)
            .maybeSingle();
        if (loadError) {
            setSettingsError(loadError.message || 'Could not load AI Assistant settings.');
            setAiAssistantSettings(DEFAULT_AI_ASSISTANT_SETTINGS);   // no saved status: the pill reads "AI: off"
            return;
        }
        if (data) {
            const [testResult, hoursResult, pauserResult] = await Promise.all([
                data.status === 'test'
                    ? supabaseClient.from('org_ai_test_contacts').select('kind, value').eq('org_id', resolvedOrgId)
                    : Promise.resolve({ data: [], error: null }),
                data.status === 'on'
                    ? supabaseClient.rpc('is_org_after_hours', { p_org: resolvedOrgId, p_at: new Date().toISOString() })
                    : Promise.resolve({ data: null, error: null }),
                // The pauser's name is not a table column: ai_integration_get resolves it (user_profiles.full_name).
                data.emergency_paused_by
                    ? supabaseClient.rpc('ai_integration_get', { p_org: resolvedOrgId })
                    : Promise.resolve({ data: null, error: null }),
            ]);
            if (testResult.error || hoursResult.error) {
                setSettingsError((testResult.error || hoursResult.error).message || 'Could not verify the AI schedule or Test list.');
            }
            setAiAssistantSettings({
                ...DEFAULT_AI_ASSISTANT_SETTINGS,
                ...data,
                test_contacts: testResult.error ? [] : testResult.data || [],
                after_hours_now: !hoursResult.error && typeof hoursResult.data === 'boolean' ? hoursResult.data : undefined,
                channels: { ...DEFAULT_AI_ASSISTANT_SETTINGS.channels, ...(data.channels || {}) },
                emergency_paused_by_name: (!pauserResult?.error && pauserResult?.data?.emergency_paused_by_name) || null,
            });
        }
    }, [resolvedOrgId]);

    useEffect(() => {
        loadSessions();
        const intervalId = setInterval(loadSessions, 10000);
        return () => clearInterval(intervalId);
    }, [loadSessions]);

    useEffect(() => {
        if (!resolvedOrgId || typeof supabaseClient.channel !== 'function') return undefined;
        const channel = supabaseClient.channel(`m9-inbox:${resolvedOrgId}`).on('postgres_changes', {
            event: '*', schema: 'public', table: 'm9_chat_signals', filter: `org_id=eq.${resolvedOrgId}`,
        }, loadSessions).subscribe();
        return () => { supabaseClient.removeChannel?.(channel); };
    }, [resolvedOrgId, loadSessions]);

    useEffect(() => {
        loadCommunicationsSettings();
        loadAiAssistantSettings();
        const intervalId = setInterval(loadAiAssistantSettings, 60000);
        return () => clearInterval(intervalId);
    }, [loadAiAssistantSettings, loadCommunicationsSettings]);

    useEffect(() => {
        let mounted = true;
        supabaseClient.auth.getUser()
            .then(({ data }) => {
                const userId = data?.user?.id || null;
                if (!mounted) return;
                setCurrentUserId(userId);
                if (userId) {
                    supabaseClient
                        .from('user_profiles')
                        .select('id, full_name, phone')
                        .in('id', [userId])
                        .then(({ data: profiles, error: profileFetchError }) => {
                            if (profileFetchError) throw profileFetchError;
                            const profile = profiles?.[0];
                            if (mounted) {
                                setProfileContact(profile?.full_name && profile?.phone
                                    ? { name: profile.full_name, phones: [profile.phone], emails: [] }
                                    : null);
                            }
                            const fullName = resolveStaffDisplayName({
                                full_name: profile?.full_name,
                                email: data?.user?.email,
                            });
                            return fullName;
                        })
                        .then((name) => {
                            if (mounted) {
                                setStaffNames((current) => ({ ...current, [userId]: name }));
                            }
                        })
                        .catch((profileError) => {
                            if (mounted) setProfileContact(null);
                            console.warn('Could not load the current staff display name.', profileError);
                        });
                } else setProfileContact(null);
            })
            .catch(() => {
                if (mounted) setCurrentUserId(null);
            });
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        if (!resolvedOrgId) {
            setSendingIdentities([]);
            return;
        }
        let mounted = true;
        Promise.all([
            supabaseClient.from('org_twilio_numbers')
                .select('id, phone_number, label, twilio_account_sid, sms_enabled, whatsapp_enabled, created_at')
                .eq('org_id', resolvedOrgId),
            supabaseClient.from('org_email_providers')
                .select('id, label, from_email, provider_type, brand_entity_id, is_default_for_brand')
                .eq('org_id', resolvedOrgId)
                .eq('is_active', true),
            // BL-177 Step 2c (F2): which numbers can send SMS from IntelAgent. A number whose texts still run through
            // the imported provider with sending switched off is shown but cannot be picked, with the reason.
            Promise.resolve(typeof supabaseClient.rpc === 'function' ? supabaseClient.rpc('bl177_sms_send_routes') : { data: null })
                .catch(() => ({ data: null })),
        ]).then(([numbersResult, emailResult, routesResult]) => {
            if (!mounted) return;
            const routeByNumberId = Object.fromEntries((Array.isArray(routesResult?.data) ? routesResult.data : [])
                .map((route) => [route.number_id, route.route || '']));
            const blockedByNumberId = Object.fromEntries((Array.isArray(routesResult?.data) ? routesResult.data : [])
                .filter((route) => route?.route === 'blocked')
                .map((route) => [route.number_id, route.reason || 'Sending from this number is not turned on.']));
            const identities = [];
            const sendingEntity = entities.find((entity) => entity?.is_booking_node) || entities[0] || null;
            const isBrandEntity = (entity) => /brand/i.test(entity?.display_label || '');
            const brandEntity = (sendingEntity && entities.find((entity) => entity?.id === sendingEntity.parent_entity_id && isBrandEntity(entity)))
                || entities.find((entity) => isBrandEntity(entity) && entity?.status === 'active')
                || sendingEntity;
            const entityName = brandEntity?.brand_name || brandEntity?.name || '';
            const entityLabel = sendingEntity?.display_label
                || entityName
                || '';
            const mainSmsLine = resolveMainSmsLine(numbersResult.data, communicationsSettings.mainSmsLine);
            (numbersResult.data || []).forEach((number) => {
                const identityLabel = number.label || number.phone_number;
                const phoneDigits = String(number.phone_number || '').replace(/\D/g, '');
                const isTestLine = /(?:test|sandbox|development)/i.test(identityLabel) || phoneDigits === '13072882778';
                const isMainLine = `+${phoneDigits}` === mainSmsLine;
                const pickerLabel = `${identityLabel}${isTestLine ? ' (test line)' : ''}`;
                if (number.sms_enabled || routeByNumberId[number.id]) {
                    identities.push({
                        id: `sms:${number.id}`,
                        sourceId: number.id,
                        channel: 'sms',
                        address: number.phone_number,
                        isRetiredSmsLine: `+${phoneDigits}` === RETIRED_SMS_LINE,
                        blockedReason: `+${phoneDigits}` === RETIRED_SMS_LINE
                            ? 'This test line is retired for SMS sending.'
                            : (blockedByNumberId[number.id] || ''),
                        route: routeByNumberId[number.id] || '',
                        label: `SMS · ${pickerLabel}`,
                        isTestLine,
                        isMainLine,
                        twilioAccountSid: number.twilio_account_sid || null,
                        entityLabel,
                        entityName,
                    });
                }
                if (number.whatsapp_enabled) {
                    identities.push({
                        id: `whatsapp:${number.id}`,
                        sourceId: number.id,
                        channel: 'whatsapp',
                        address: number.phone_number,
                        label: `WhatsApp · ${pickerLabel}`,
                        isTestLine,
                        isMainLine,
                        twilioAccountSid: number.twilio_account_sid || null,
                        entityLabel,
                        entityName,
                    });
                }
            });
            const emailProviders = [...(emailResult.data || [])].sort((left, right) => {
                const leftDefault = left.is_default_for_brand && left.brand_entity_id === brandEntity?.id;
                const rightDefault = right.is_default_for_brand && right.brand_entity_id === brandEntity?.id;
                return Number(rightDefault) - Number(leftDefault);
            });
            emailProviders.forEach((provider) => {
                identities.push({
                    id: `email:${provider.id}`,
                    sourceId: provider.id,
                    channel: 'email',
                    address: provider.from_email,
                    label: `Email · ${provider.label || provider.from_email || provider.provider_type}`,
                    brandEntityId: provider.brand_entity_id || null,
                    entityLabel: entities.find((entity) => entity?.id === provider.brand_entity_id)?.display_label || entityLabel,
                    entityName: entities.find((entity) => entity?.id === provider.brand_entity_id)?.brand_name
                        || entities.find((entity) => entity?.id === provider.brand_entity_id)?.name
                        || entityName,
                    isDefaultForBrand: Boolean(provider.is_default_for_brand && provider.brand_entity_id === brandEntity?.id),
                });
            });
            setSendingIdentities(identities);
        }).catch((identityError) => {
            if (mounted) setComposeError(identityError.message || 'Could not load sending identities.');
        });
        return () => { mounted = false; };
    }, [entities, resolvedOrgId, communicationsSettings.mainSmsLine]);

    useEffect(() => {
        if (!resolvedOrgId) return;
        let mounted = true;
        setTemplatesLoading(true);
        supabaseClient
            .from('message_templates')
            .select('id, org_id, channel, name, display_name, body, variables, header, footer, buttons, email_subject, status, archived_at, twilio_content_sid, twilio_account_sid, updated_at')
            .order('updated_at', { ascending: false })
            .then(({ data, error: templateError }) => {
                if (!mounted) return;
                if (templateError) throw templateError;
                setTemplates((Array.isArray(data) ? data : [])
                    .filter((row) => row?.org_id === resolvedOrgId && !row?.archived_at)
                    .map((row) => ({
                    id: row.id,
                    sid: row.id,
                    contentSid: row.twilio_content_sid || '',
                    twilioAccountSid: row.twilio_account_sid || null,
                    channel: row.channel,
                    name: row.name,
                    display_name: row.display_name || '',
                    body: row.body || '',
                    variables: row.variables || {},
                    header: row.header || { type: 'none' },
                    footer: row.footer || '',
                    buttons: Array.isArray(row.buttons) ? row.buttons : [],
                    emailSubject: row.email_subject || '',
                    status: row.status,
                    archived_at: row.archived_at,
                    updated_at: row.updated_at,
                })));
            })
            .catch((templateError) => {
                if (mounted) setComposeError(templateError.message || 'Could not load approved templates.');
            })
            .finally(() => {
                if (mounted) setTemplatesLoading(false);
            });
        return () => { mounted = false; };
    }, [resolvedOrgId]);

    const handleSetFlag = useCallback(async (sessionId, { status, active = false, note = '' } = {}) => {
        if (!sessionId) return;
        let userId = null;
        try {
            const { data: authData } = await supabaseClient.auth.getUser();
            userId = authData?.user?.id || null;
        } catch {
            // An unresolved staff id still records the flag; flagged_by simply stays null.
        }
        const previousSession = sessions.find((session) => session.id === sessionId) || {};
        const now = new Date();
        const patch = buildFlagPatch({ status, active, userId, now });
        const noteEntries = buildStatusTimelineEntries({
            previousSession,
            status: patch.flag_status,
            active: patch.flag_active,
            note,
            userId,
            userLabel: staffNames[userId] || 'Staff',
            now,
        });
        patch.internal_notes = appendInternalNotes(previousSession.internal_notes, noteEntries);
        // BL-177 Gate 17c: any status other than Closed on a closed thread also reopens it.
        const reopenPatch = buildManualReopenPatch(previousSession, patch.flag_status);
        const fullPatch = { ...patch, ...reopenPatch };
        // Optimistic: the row's icon flips immediately, then the next poll confirms it.
        setSessions((previous) => previous.map((session) => (session.id === sessionId ? { ...session, ...fullPatch } : session)));
        let { error: flagError } = await supabaseClient
            .from('intake_sessions')
            .update(fullPatch)
            .eq('id', sessionId);
        // idx_intake_sessions_open_thread allows one open session per org/channel/thread. If a
        // newer session already holds that slot, keep this one closed and save just the status.
        if (flagError?.code === '23505' && Object.keys(reopenPatch).length > 0) {
            ({ error: flagError } = await supabaseClient
                .from('intake_sessions')
                .update(patch)
                .eq('id', sessionId));
            if (!flagError) loadSessions();
        }
        if (flagError) {
            setFlagError(flagError.message);
            loadSessions();
            return;
        }
        setFlagError(null);
    }, [loadSessions, sessions, staffNames]);

    const handleMarkInternalNotesRead = useCallback(async (sessionId) => {
        if (!sessionId || !currentUserId) return;
        const previousSession = sessions.find((session) => session.id === sessionId) || {};
        const patch = {
            internal_notes_read_by: buildInternalNotesReadBy(previousSession.internal_notes_read_by, currentUserId),
        };
        setSessions((previous) => previous.map((session) => (session.id === sessionId ? { ...session, ...patch } : session)));
        const { error: readError } = await supabaseClient
            .from('intake_sessions')
            .update(patch)
            .eq('id', sessionId);
        if (readError) {
            setFlagError(readError.message);
            loadSessions();
        }
    }, [currentUserId, loadSessions, sessions]);

    // Staff Messaging Phase 1: a note may @mention colleagues ([{user_id, label}]). The database alerts them (once per person per
    // note) when the note is saved; one save at a time per conversation, so a double tap is one note.
    const noteSavingRef = useRef(new Set());
    const handleAddInternalNote = useCallback(async (sessionId, note, mentions = []) => {
        if (!sessionId || noteSavingRef.current.has(sessionId)) return false;
        const previousSession = sessions.find((session) => session.id === sessionId) || {};
        const entry = buildManualInternalNote({
            note,
            userId: currentUserId,
            userLabel: staffNames[currentUserId] || 'Staff',
            mentions,
        });
        if (!entry) return false;
        noteSavingRef.current.add(sessionId);
        try {
            const patch = {
                internal_notes: appendInternalNotes(previousSession.internal_notes, [entry]),
                internal_notes_read_by: buildInternalNotesReadBy(previousSession.internal_notes_read_by, currentUserId, entry.createdAt),
            };
            setSessions((previous) => previous.map((session) => (session.id === sessionId ? { ...session, ...patch } : session)));
            const { error: noteError } = await supabaseClient
                .from('intake_sessions')
                .update(patch)
                .eq('id', sessionId);
            if (noteError) {
                setFlagError(noteError.message);
                loadSessions();
                return false;
            }
            setFlagError(null);
            return true;
        } finally {
            noteSavingRef.current.delete(sessionId);
        }
    }, [currentUserId, loadSessions, sessions, staffNames]);

    const handleOpenNewMessage = useCallback(({ initialSearch = '' } = {}) => {
        setComposeOpen(true);
        setComposeError('');
        setContactSearch(initialSearch);
    }, []);

    const rememberReplyDraft = useCallback((sessionId, draft = {}) => {
        const body = typeof draft?.body === 'string' ? draft.body.trim() : '';
        if (!sessionId || !body) return;
        setReplyDraftsBySession((previous) => ({
            ...previous,
            [sessionId]: {
                body,
                subject: typeof draft?.subject === 'string' ? draft.subject.trim() : '',
            },
        }));
    }, []);

    const handleHandBackToAi = useCallback(async (sessionId) => {
        const patch = {
            ai_paused_until: null,
            ai_paused_by: null,
            ai_pause_reason: null,
        };
        const { error: handBackError } = await supabaseClient
            .from('intake_sessions')
            .update(patch)
            .eq('id', sessionId);
        if (handBackError) {
            setError(handBackError.message || 'Could not hand this thread back to the AI.');
            return;
        }
        setSessions((current) => current.map((session) => (
            session.id === sessionId ? { ...session, ...patch } : session
        )));
    }, []);

    const handleOpenThread = useCallback((sessionId, draft = {}) => {
        rememberReplyDraft(sessionId, draft);
        setSelectedSessionId(sessionId);
        announceConversationEngaged(sessionId);
        setComposeOpen(false);
        setComposeError('');
        setThreadContactError('');
    }, [rememberReplyDraft]);
    // BL-177 Step 2d (section 6): the in-app pop-up's Reply fires `ia:reply` {threadUrl, sessionId}. If that thread
    // isn't the open one, open it, then put the cursor in its reply box once the composer has rendered (checked a few
    // times, ~1.5 s at most). The composer handles the already-open case itself.
    const commsRootRef = useRef(null);
    const commsViewport = useCommsViewport();
    // phones: the open conversation fills the screen and its composer rides just above the on-screen keyboard
    useVisualViewportVars(commsRootRef, commsViewport === 'phone');
    const selectedSessionIdRef = useRef(selectedSessionId);
    selectedSessionIdRef.current = selectedSessionId;
    const handleOpenThreadRef = useRef(handleOpenThread);
    handleOpenThreadRef.current = handleOpenThread;
    useEffect(() => {
        // Subscribed once; opening the thread re-renders the view but must not cancel the pending focus.
        const timers = [];
        const focusReplyBox = () => {
            let tries = 0;
            const focusWhenReady = () => {
                const textarea = commsRootRef.current?.querySelector('textarea[aria-label="Reply message"]');
                if (textarea && !textarea.disabled) {
                    textarea.focus();
                    return;
                }
                tries += 1;
                if (tries < 30) timers.push(setTimeout(focusWhenReady, 50));
            };
            timers.push(setTimeout(focusWhenReady, 0));
        };
        const onReply = (event) => {
            const sessionId = event?.detail?.sessionId ? String(event.detail.sessionId) : '';
            if (!sessionId || sessionId === selectedSessionIdRef.current) return;
            setWorkspaceTab('conversations');
            handleOpenThreadRef.current(sessionId);
            focusReplyBox();
        };
        window.addEventListener('ia:reply', onReply);
        // BL-177 Step 2e (item 8): the push banner's "Reply" button on a closed app opens /communications?session=...&reply=1.
        // Put the cursor in that conversation's reply box once it has rendered, then drop the flag so a refresh doesn't repeat it.
        try {
            const params = new URLSearchParams(window.location.search);
            if (params.get('reply') === '1' && params.get('session')) {
                params.delete('reply');
                const query = params.toString();
                window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
                focusReplyBox();
            }
        } catch { /* no history API: the conversation still opens */ }
        return () => {
            window.removeEventListener('ia:reply', onReply);
            timers.forEach(clearTimeout);
        };
    }, []);
    // Comms next pass (5b): "Add to CRM" on a Not-in-CRM thread opens it with the contact form open, prefilled.
    const [contactEditorRequest, setContactEditorRequest] = useState(null);
    const handleAddToCrm = useCallback((thread) => {
        if (!thread?.id) return;
        handleOpenThread(thread.id);
        setContactEditorRequest({ sessionId: thread.id, nonce: Date.now() });
    }, [handleOpenThread]);
    const handleEmailReply = useCallback((sessionId) => {
        setWorkspaceTab('conversations');
        handleOpenThread(sessionId);
    }, [handleOpenThread]);

    // Comms final pass (item 5): a notification's View must always open its conversation, whether Communications is already on
    // screen or not, on every screen size (on a phone, selecting it switches to the conversation screen). The bell and push
    // banners navigate in-app (pushState + popstate) and also announce OPEN_CONVERSATION_EVENT. A conversation that is not in the
    // loaded list (it just arrived, it is archived, or the list has not loaded yet) is fetched on its own; one this person cannot
    // read (a colleague's private conversation) says so instead of leaving a blank screen.
    const [openNotice, setOpenNotice] = useState('');
    const lastOpenRef = useRef({ id: '', at: 0 });
    const sessionsRef = useRef(sessions);
    sessionsRef.current = sessions;
    const openConversationById = useCallback(async (rawId) => {
        const sessionId = String(rawId || '').toLowerCase();
        if (!sessionId) return;
        // the bell both navigates (popstate) and announces the open: one View is one open
        const now = Date.now();
        if (lastOpenRef.current.id === sessionId && now - lastOpenRef.current.at < 400) return;
        lastOpenRef.current = { id: sessionId, at: now };
        setWorkspaceTab('conversations');
        setComposeOpen(false);
        setOpenNotice('');
        handleOpenThreadRef.current(sessionId);
        if (sessionsRef.current.some((session) => session.id === sessionId)) return;
        try {
            const { data } = await supabaseClient.from('intake_sessions').select(SESSION_COLUMNS).eq('id', sessionId).maybeSingle();
            if (data?.id) {
                setSessions((previous) => (previous.some((session) => session.id === data.id) ? previous : [data, ...previous]));
                return;
            }
        } catch { /* fall through to the notice */ }
        const status = await loadGateStatus(supabaseClient, sessionId);
        if (selectedSessionIdRef.current !== sessionId) return;
        setOpenNotice(openNoticeText(status));
    }, []);
    // Staff Messaging Phase 1: a mention alert (bell or push) opens its conversation scrolled to that note, highlighted briefly.
    const [noteFocus, setNoteFocus] = useState(() => {
        const sessionId = sessionIdFromSearch(globalThis.location?.search);
        const noteId = noteIdFromSearch(globalThis.location?.search);
        return sessionId && noteId ? { sessionId, noteId, nonce: 1 } : null;
    });
    const requestNoteFocus = useCallback((sessionId, noteId) => {
        const safe = typeof noteId === 'string' && /^[A-Za-z0-9_-]{1,120}$/.test(noteId) ? noteId : '';
        if (!sessionId || !safe) return;
        setNoteFocus((current) => ({ sessionId: String(sessionId).toLowerCase(), noteId: safe, nonce: (current?.nonce || 0) + 1 }));
    }, []);
    const openConversationByIdRef = useRef(openConversationById);
    openConversationByIdRef.current = openConversationById;
    useEffect(() => {
        const fromLocation = () => {
            if (!/^\/communications\/?$/.test(window.location.pathname)) return;
            const sessionId = sessionIdFromSearch(window.location.search);
            if (sessionId) {
                requestNoteFocus(sessionId, noteIdFromSearch(window.location.search));
                openConversationByIdRef.current(sessionId);
            }
        };
        const onOpen = (event) => {
            const sessionId = event?.detail?.sessionId;
            if (sessionId) {
                requestNoteFocus(sessionId, event?.detail?.noteId);
                openConversationByIdRef.current(sessionId);
            }
        };
        window.addEventListener('popstate', fromLocation);
        window.addEventListener(OPEN_CONVERSATION_EVENT, onOpen);
        return () => {
            window.removeEventListener('popstate', fromLocation);
            window.removeEventListener(OPEN_CONVERSATION_EVENT, onOpen);
        };
    }, [requestNoteFocus]);

    // Staff Messaging Phase 1: the "@" marker in the inbox for conversations where I was mentioned and have not looked since.
    // Read with the list (at most every 15 s); opening the conversation clears it (on the server too).
    const [mentionedSessionIds, setMentionedSessionIds] = useState(() => new Set());
    const mentionReadAtRef = useRef(0);
    useEffect(() => {
        if (!currentUserId) return undefined;
        const now = Date.now();
        if (now - mentionReadAtRef.current < 15000) return undefined;
        mentionReadAtRef.current = now;
        let active = true;
        loadUnseenMentionSessions(supabaseClient).then((ids) => {
            if (!active) return;
            const open = selectedSessionIdRef.current;
            if (open && ids.has(open)) ids.delete(open);
            setMentionedSessionIds(ids);
        });
        return () => { active = false; };
    }, [currentUserId, sessions]);
    useEffect(() => {
        if (!selectedSessionId || !mentionedSessionIds.has(selectedSessionId)) return;
        setMentionedSessionIds((current) => {
            const next = new Set(current);
            next.delete(selectedSessionId);
            return next;
        });
        markMentionsSeen(supabaseClient, selectedSessionId);
    }, [mentionedSessionIds, selectedSessionId]);
    // the first link (?session= on arrival) gets the same treatment once the list has loaded
    const initialLinkRef = useRef(sessionIdFromSearch(globalThis.location?.search));
    useEffect(() => {
        if (loading || !initialLinkRef.current) return;
        const sessionId = initialLinkRef.current;
        initialLinkRef.current = null;
        if (!sessions.some((session) => session.id === sessionId)) openConversationByIdRef.current(sessionId);
    }, [loading, sessions]);

    // ---- BL-177 Step 2l: Company inbox / My inbox, Take / Leave / Pass to, join requests and invites ----
    const access = useConversationAccess({ client: supabaseClient, userId: currentUserId });
    const [inboxScope, setInboxScope] = useState('company');
    const [accessBusy, setAccessBusy] = useState(false);
    const [accessError, setAccessError] = useState('');
    const [accessModal, setAccessModal] = useState(null);
    const [accessModalError, setAccessModalError] = useState('');
    const [invitable, setInvitable] = useState({ rows: null, loading: false });
    const [people, setPeople] = useState({ sessionId: null, rows: [], loading: false, error: '' });

    const runAccess = useCallback(async (action, { thenScope = '' } = {}) => {
        setAccessBusy(true);
        setAccessError('');
        try {
            await action();
            await access.reload();
            await loadSessions();
            announceAccessChanged();
            if (thenScope) setInboxScope(thenScope);
            return true;
        } catch (failure) {
            setAccessError(failure.message || 'That did not work. Please try again.');
            access.reload();
            return false;
        } finally {
            setAccessBusy(false);
        }
    }, [access, loadSessions]);

    const handleTake = useCallback(async (sessionId) => {
        const taken = await runAccess(
            () => takeConversation(supabaseClient, { sessionId, userId: currentUserId, orgId: resolvedOrgId }),
            { thenScope: 'mine' },
        );
        if (taken) announceConversationEngaged(sessionId);
        return taken;
    }, [currentUserId, resolvedOrgId, runAccess]);
    const handleLeave = useCallback((sessionId) => runAccess(
        () => leaveConversation(supabaseClient, { sessionId, userId: currentUserId }),
        { thenScope: 'company' },
    ), [currentUserId, runAccess]);
    const handleWithdraw = useCallback((requestId) => runAccess(() => withdrawRequest(supabaseClient, { requestId })), [runAccess]);
    // BL-177 Step 2j (B1): the host closes a group conversation: everyone but the host is removed (they get a short alert) and it is
    // one-on-one again. The thread stays open. The database checks that the caller is the host.
    const handleCloseGroup = useCallback((sessionId) => runAccess(() => closeGroupConversation(supabaseClient, { sessionId })), [runAccess]);
    // Comms final pass: Gated Group Communications. Release (the owner, or an admin) puts the conversation back in the Company inbox.
    const handleRelease = useCallback((sessionId) => runAccess(
        () => releaseConversation(supabaseClient, { sessionId }),
        { thenScope: 'company' },
    ), [runAccess]);

    const openAccessModal = useCallback((kind, sessionId, { toUserId = '' } = {}) => {
        setAccessModal({ kind, sessionId, toUserId });
        setAccessModalError('');
        if (kind === 'join') return;
        setInvitable({ rows: null, loading: true });
        listInvitable(supabaseClient, sessionId)
            .then((rows) => setInvitable({ rows, loading: false }))
            .catch((failure) => { setInvitable({ rows: [], loading: false }); setAccessModalError(failure.message); });
    }, []);

    const submitAccessModal = useCallback(async ({ note, toUserId }) => {
        if (!accessModal) return;
        const { kind, sessionId } = accessModal;
        setAccessBusy(true);
        setAccessModalError('');
        try {
            if (kind === 'join') await requestToJoin(supabaseClient, { sessionId, note });
            else if (kind === 'pass') await passTo(supabaseClient, { sessionId, toUserId, note });
            else await inviteColleague(supabaseClient, { sessionId, toUserId, note });
            setAccessModal(null);
            await access.reload();
            announceAccessChanged();
        } catch (failure) {
            setAccessModalError(failure.message || 'That did not work. Please try again.');
        } finally {
            setAccessBusy(false);
        }
    }, [access, accessModal]);

    useEffect(() => {
        setPreviewLines(readPreviewLines(currentUserId));
    }, [currentUserId]);

    useEffect(() => {
        if (!currentUserId) return undefined;
        let active = true;
        loadEnterKeyPreference(supabaseClient, currentUserId)
            .then((mode) => { if (active) setEnterKeyMode(mode); })
            .catch(() => { /* keep the default (Send) if the saved choice cannot be read */ });
        return () => { active = false; };
    }, [currentUserId]);

    // Per-user, browser-only view choice: applies the moment it's picked, not on "Save settings",
    // so it never depends on the org settings save (owner/admin + not a read-only support session).
    const handlePreviewLinesChange = useCallback((next) => {
        setPreviewLines(next);
        writePreviewLines(currentUserId, next);
    }, [currentUserId]);

    const isCompanyAdmin = useIsCompanyAdmin(supabaseClient);
    const handleSaveSettings = useCallback(async (values) => {
        if (!resolvedOrgId) {
            setSettingsError('Could not save Communications settings: missing org scope.');
            return;
        }
        setSettingsSaving(true);
        setSettingsError('');
        const nextSettings = normalizeCommunicationsSettings(values);
        setCommunicationsSettings(nextSettings);
        try {
            // Review fix round 2: only an Owner / Tenant Admin sends the daily number-check limit (the database guards it too).
            const payload = buildCommunicationsSettingsUpsert({
                orgId: resolvedOrgId, values: nextSettings, includeNumberCheckDailyLimit: isCompanyAdmin,
            });
            const saveWith = (row) => withComms292Fallback((columns, patch) => supabaseClient
                .from('org_communications_settings')
                .upsert(patch, { onConflict: 'org_id' }), '', row);
            let { error: saveError } = await saveWith(payload);
            // Review fix round 1: before migration 281 is applied, save everything else.
            if (isMissingMigration281ColumnError(saveError)) ({ error: saveError } = await saveWith(withoutMigration281Columns(payload)));
            if (saveError) {
                setSettingsError(saveError.message || 'Could not save Communications settings.');
                await loadCommunicationsSettings();
                return;
            }
            setSettingsOpen(false);
        } catch (saveError) {
            setSettingsError(saveError.message || 'Could not save Communications settings.');
            await loadCommunicationsSettings();
        } finally {
            setSettingsSaving(false);
        }
    }, [isCompanyAdmin, loadCommunicationsSettings, resolvedOrgId]);

    // Comms gate fixes: a send can claim the conversation (Gated Group Communications). Re-read who owns it straight away, so the
    // "You own this conversation" chip and the move to My inbox show at once instead of on the next poll. (A ref, because the gate
    // loader is declared further down.)
    const refreshGateAfterSendRef = useRef(null);
    const handleReplySent = useCallback((entry) => {
        if (entry?.client_ref && selectedSessionId) announceConversationEngaged(selectedSessionId);
        if (entry?.client_ref) refreshGateAfterSendRef.current?.();
        if (selectedSessionId) {
            setReplyDraftsBySession((previous) => {
                if (!previous[selectedSessionId]) return previous;
                const next = { ...previous };
                delete next[selectedSessionId];
                return next;
            });
        }
        loadSessions();
    }, [loadSessions, selectedSessionId]);

    const handleSendMessage = useCallback(async ({
        recipients = [],
        identity,
        channel,
        messageType,
        subject,
        body,
        mediaUrl,
        template,
        templateVariables,
        templateBody,
        signature = null,
        groupPhones = null,
        eventId = null,
        requestKey = null,
        sendingIdentityExplicitlyChosen = false,
    } = {}) => {
        if (!currentUserId || !resolvedOrgId) {
            setComposeError('Could not send: missing staff or organization scope.');
            return null;
        }
        const saveExtraRecipients = async () => {
            const failures = [];
            for (const { contact } of recipients.filter((entry) => entry.contact?.isNewRecipient && entry.contact.saveAsContact)) {
                try {
                    const address = contact.emails?.[0] || contact.phones?.[0];
                    if (contacts.some((known) => (known.emails || []).includes(address) || (known.phones || []).includes(address))) continue;
                    const row = buildNewContactInsert({ name: contact.name, firstName:contact.first_name, lastName:contact.last_name, phone: contact.phones?.[0] || '', email: contact.emails?.[0] || '', entityId: contact.entity_id || defaultEntityId, requiresHumanReview: true });
                    const { data, error: failure } = await withComms292Fallback((columns, rows) => supabaseClient.from('contacts').insert(rows).select(columns).single(), CONTACT_COLUMNS, [row]);
                    if (failure || !data) throw failure || new Error('Contact could not be saved.');
                    setContacts((items) => [data, ...items.filter((item) => item.id !== data.id)]);
                } catch { failures.push(contact.emails?.[0] || contact.phones?.[0]); }
            }
            return failures.length ? ` Contact saving needs attention: ${failures.join(', ')}.` : '';
        };
        setStartingThread(true);
        setComposeError('');
        // BL-177 Step 2e (6b): ONE group text to everyone: one the imported provider send, one shared thread. Never one text per person.
        if (messageType === 'group' && Array.isArray(groupPhones) && groupPhones.length >= 2) {
            const loadThread = async (threadId) => {
                const { data: row } = await supabaseClient.from('intake_sessions').select(SESSION_COLUMNS).eq('id', threadId).single();
                return row || null;
            };
            const openThread = (thread) => {
                if (!thread) return;
                setSessions((previous) => [thread, ...previous.filter((item) => item.id !== thread.id)]);
                setSelectedSessionId(thread.id);
                setComposeOpen(false);
            };
            try {
                const groupResult = await sendNewGroupText({
                    orgId: resolvedOrgId, groupPhones, identity, body, mediaUrl, signature, sendingIdentityExplicitlyChosen,
                    sendReply: sendStaffReply,
                    signBody: (input) => signOutgoingBody({ client: supabaseClient, ...input }),
                    loadThread,
                });
                openThread(groupResult.thread);
                loadSessions();
                return { sent: 1, skipped: 0, notice: groupResult.notice + await saveExtraRecipients() };
            } catch (groupSendError) {
                openThread(groupSendError?.thread);
                setComposeError(groupSendError?.message || 'The group text could not be sent.');
                return null;
            } finally {
                setStartingThread(false);
            }
        }
        if (recipients.length > BULK_QUEUE_THRESHOLD || messageType === 'service_update') {
            try {
                if (mediaUrl || (channel === 'whatsapp' && template)) throw new Error('Queued sends support text and email bodies. Remove attachments and WhatsApp templates.');
                const signed = await signOutgoingBody({ body, signature });
                const bodies = template ? Object.fromEntries(recipients.map(({ contact, option }) => {
                    const resolved = buildTemplateVariableValues(template, { contact, session: findActiveThreadForContact(contact, sessions) }, { useSampleFallback: false });
                    const values = { ...(templateVariables || {}) };
                    Object.entries(template.variables || {}).forEach(([key, config]) => { if (config?.source?.startsWith('contact.')) values[key] = resolved[key] || ''; });
                    return [option.address, fillTemplateBody(templateBody || template.body || body, values)];
                })) : {};
                const result = await enqueueBulk(supabaseClient, { orgId: resolvedOrgId, requestKey: requestKey || crypto.randomUUID(),
                    channel, messageType, subject, body: typeof signed === 'string' ? signed : signed.body,
                    bodies, sendingIdentityId: identity?.sourceId, addresses: recipients.map((entry) => entry.option?.address).filter(Boolean), eventId });
                return { ...result, notice: 'Queued. You can keep working while messages are sent.' + await saveExtraRecipients() };
            } catch (failure) { setComposeError(failure.message); return null; }
            finally { setStartingThread(false); }
        }
        const deliveryChannel = channel === 'sms' && mediaUrl ? 'mms' : channel;
        const contentSid = channel === 'whatsapp' && messageType !== 'general'
            ? (template?.contentSid || template?.sid || '')
            : '';
        const fanout = buildRecipientFanout({
            contacts: recipients.map((recipient) => recipient.contact),
            channel,
            messageType,
        });
        let campaignId = null;
        let sent = 0;
        let createdLead = false;
        const skipped = fanout.filter((recipient) => recipient.skipped).length;
        try {
            if (messageType === 'campaign') {
                const { data: campaign, error: campaignError } = await supabaseClient
                    .from('comm_campaigns')
                    .insert([{
                        org_id: resolvedOrgId,
                        created_by: currentUserId,
                        channel: deliveryChannel,
                        message_type: messageType,
                        sending_identity_id: identity?.sourceId || null,
                        sending_address: identity?.address || null,
                        body: body || null,
                        content_sid: contentSid || null,
                        content_variables: templateVariables || {},
                        recipient_count: fanout.length,
                        skipped_count: skipped,
                        status: 'sending',
                        started_at: new Date().toISOString(),
                    }])
                    .select('id')
                    .single();
                if (campaignError || !campaign?.id) throw new Error(campaignError?.message || 'Could not create the campaign.');
                campaignId = campaign.id;
            }

            for (const recipient of fanout) {
                const selected = recipients.find((entry) => entry.contact.id === recipient.contact.id);
                if (recipient.skipped) {
                    if (campaignId) {
                        const { error: skippedInsertError } = await supabaseClient
                            .from('comm_campaign_recipients')
                            .insert([{
                                org_id: resolvedOrgId,
                                campaign_id: campaignId,
                                contact_id: recipient.contact.isNewRecipient ? null : recipient.contact.id,
                                destination: recipient.option?.address || selected?.option?.address || '',
                                status: 'skipped',
                                skipped_reason: recipient.skippedReason,
                            }]);
                        if (skippedInsertError) throw new Error(skippedInsertError.message);
                    }
                    continue;
                }
                let contact = recipient.contact;
                let newAccount = null;
                let newContact = null;
                let session = findActiveThreadForContact(contact, sessions);
                let campaignRecipientId = null;
                try {
                    if (!session && campaignId && !contact.isNewRecipient) {
                        const { data: campaignRecipient, error: recipientError } = await supabaseClient
                            .from('comm_campaign_recipients')
                            .insert([{
                                org_id: resolvedOrgId,
                                campaign_id: campaignId,
                                contact_id: contact.id,
                                intake_session_id: null,
                                destination: recipient.option?.address || selected?.option?.address || '',
                                status: 'queued',
                            }])
                            .select('id')
                            .single();
                        if (recipientError) throw new Error(recipientError.message);
                        campaignRecipientId = campaignRecipient?.id || null;
                    }

                    // Apply the configured signature to each eligible outbound message.
                    let recipientTemplateBody = body;
                    if (template && messageType === 'campaign') {
                        const resolved = buildTemplateVariableValues(
                            template,
                            { contact, trip: session, session },
                            { useSampleFallback: template?.channel !== 'sms' }
                        );
                        const values = { ...(templateVariables || {}) };
                        Object.entries(template.variables || {}).forEach(([key, config]) => {
                            if (typeof config?.source === 'string' && config.source.startsWith('contact.')) {
                                values[key] = resolved[key] || '';
                            }
                        });
                        recipientTemplateBody = fillTemplateBody(templateBody || body, values);
                    }
                    const signing = await signOutgoingBody({
                        client: supabaseClient,
                        body: recipientTemplateBody,
                        signature,
                        channel,
                        isTemplate: Boolean(template),
                        sessionId: session?.id || null,
                    });
                    const recipientBody = signing.body;
                    let sendResult;
                    try {
                    sendResult = await sendStaffReply({
                        session,
                        orgId: resolvedOrgId,
                        recipientAddress: recipient.option?.address || selected?.option?.address || '',
                        channel: deliveryChannel,
                        body: recipientBody,
                        ...(channel === 'email' ? {html: template ? plainTextToEmailHtml(recipientTemplateBody) : appendSignatureHtml(plainTextToEmailHtml(recipientTemplateBody),signature)} : {}),
                        subject: channel === 'email' ? subject : '',
                        mediaUrl,
                        contentSid,
                        contentVariables: contentSid ? templateVariables : null,
                        templateBody: contentSid ? templateBody : '',
                        sendingIdentityId: identity?.sourceId || null,
                        sendingIdentityExplicitlyChosen,
                        campaignRecipientId,
                    });
                    } catch (firstSendError) {
                        // BL-177 Step 2c: a first the imported provider text that failed still created its thread. Keep it and open it,
                        // so the failed bubble (with its reason and Retry) is what the person sees, never an empty thread.
                        if (!session && firstSendError?.sessionId) {
                            const { data: failedThread } = await supabaseClient
                                .from('intake_sessions').select(SESSION_COLUMNS).eq('id', firstSendError.sessionId).single();
                            if (failedThread) {
                                let shown = failedThread;
                                if (contact.primary_account_id && !failedThread.account_id) {
                                    const { data: linked } = await supabaseClient
                                        .from('intake_sessions').update({ account_id: contact.primary_account_id }).eq('id', failedThread.id)
                                        .select(SESSION_COLUMNS).single();
                                    if (linked) shown = linked;
                                }
                                setSessions((previous) => [shown, ...previous.filter((item) => item.id !== shown.id)]);
                                setSelectedSessionId(shown.id);
                                setComposeOpen(false);
                            }
                        }
                        throw firstSendError;
                    }
                    if (sendResult?.sent !== true) throw new Error('The message was not confirmed as sent.');
                    if (!session && sendResult?.sessionId) {
                        // BL-177 Step 2c (F2): the send itself created the conversation (the imported provider route). Use that row
                        // instead of inserting a second one for the same thread.
                        const { data: createdThread, error: createdError } = await supabaseClient
                            .from('intake_sessions').select(SESSION_COLUMNS).eq('id', sendResult.sessionId).single();
                        if (createdError || !createdThread) throw new Error(createdError?.message || 'Message sent, but its conversation could not be loaded.');
                        // F6: saving the number as a CRM lead is optional (the "Save as a contact" box); sending never needs it
                        if (contact.isNewRecipient && contact.saveAsContact !== false) {
                            const placeholder = contact.name || contact.phones?.[0] || contact.emails?.[0] || 'New lead';
                            const { data: account, error: accountError } = await supabaseClient
                                .from('accounts')
                                .insert([buildNewAccountInsert({ accountName: placeholder, entityId: contact.entity_id || defaultEntityId, isBusiness: false })])
                                .select(ACCOUNT_COLUMNS)
                                .single();
                            if (accountError || !account?.id) throw new Error(accountError?.message || 'Message sent, but the new CRM lead could not be saved.');
                            const contactInsert = buildNewContactInsert({
                                name: placeholder, firstName:contact.first_name, lastName:contact.last_name, phone: contact.phones?.[0] || '', email: contact.emails?.[0] || '',
                                entityId: contact.entity_id || defaultEntityId, primaryAccountId: account.id, requiresHumanReview: true,
                            });
                            // BL-177 Step 2m: no preference or consent columns from staff; the client sets their own.
                            const { data: insertedContact, error: contactError } = await withComms292Fallback((columns, rows) => supabaseClient.from('contacts').insert(rows).select(columns).single(), CONTACT_COLUMNS, [contactInsert]);
                            if (contactError || !insertedContact?.id) throw new Error(contactError?.message || 'Message sent, but the new CRM contact could not be saved.');
                            const { error: primaryContactError } = await supabaseClient
                                .from('accounts').update({ primary_contact_id: insertedContact.id }).eq('id', account.id);
                            if (primaryContactError) throw new Error(primaryContactError.message || 'Message sent, but the CRM lead could not be linked to its contact.');
                            newAccount = account;
                            newContact = insertedContact;
                            contact = { ...insertedContact, primary_account_id: account.id };
                        }
                        let linkedThread = createdThread;
                        if (contact.primary_account_id && !createdThread.account_id) {
                            const { data: linked } = await supabaseClient
                                .from('intake_sessions').update({ account_id: contact.primary_account_id }).eq('id', createdThread.id)
                                .select(SESSION_COLUMNS).single();
                            if (linked) linkedThread = linked;
                        }
                        session = linkedThread;
                        setSessions((previous) => [linkedThread, ...previous.filter((item) => item.id !== linkedThread.id)]);
                        setSelectedSessionId(linkedThread.id);
                        setComposeOpen(false);
                        if (newAccount && newContact) {
                            setAccounts((current) => [{ ...newAccount, primary_contact_id: newContact.id }, ...current.filter((row) => row.id !== newAccount.id)]);
                            setContacts((current) => [{ ...newContact, primary_account_id: newAccount.id }, ...current.filter((row) => row.id !== newContact.id)]);
                            createdLead = true;
                        }
                    } else if (!session) {
                        // F6: saving the number as a CRM lead is optional (the "Save as a contact" box); sending never needs it
                        if (contact.isNewRecipient && contact.saveAsContact !== false) {
                            const placeholder = contact.name || contact.phones?.[0] || contact.emails?.[0] || 'New lead';
                            const accountInsert = buildNewAccountInsert({
                                accountName: placeholder,
                                entityId: contact.entity_id || defaultEntityId,
                                isBusiness: false,
                            });
                            const { data: account, error: accountError } = await supabaseClient
                                .from('accounts')
                                .insert([accountInsert])
                                .select(ACCOUNT_COLUMNS)
                                .single();
                            if (accountError || !account?.id) throw new Error(accountError?.message || 'Message sent, but the new CRM lead could not be saved.');
                            newAccount = account;
                            const contactInsert = buildNewContactInsert({
                                name: placeholder,
                                firstName:contact.first_name, lastName:contact.last_name,
                                phone: contact.phones?.[0] || '',
                                email: contact.emails?.[0] || '',
                                entityId: contact.entity_id || defaultEntityId,
                                primaryAccountId: account.id,
                                requiresHumanReview: true,
                            });
                            // BL-177 Step 2m: no preference or consent columns from staff; the client sets their own.
                            const { data: insertedContact, error: contactError } = await withComms292Fallback((columns, rows) => supabaseClient.from('contacts').insert(rows).select(columns).single(), CONTACT_COLUMNS, [contactInsert]);
                            if (contactError || !insertedContact?.id) throw new Error(contactError?.message || 'Message sent, but the new CRM contact could not be saved.');
                            newContact = insertedContact;
                            const { error: primaryContactError } = await supabaseClient
                                .from('accounts')
                                .update({ primary_contact_id: insertedContact.id })
                                .eq('id', account.id);
                            if (primaryContactError) throw new Error(primaryContactError.message || 'Message sent, but the CRM lead could not be linked to its contact.');
                            contact = { ...insertedContact, primary_account_id: account.id };
                        }

                        const sentAt = new Date().toISOString();
                        const logEntry = {
                            direction: 'outbound',
                            channel: deliveryChannel,
                            ...(sendResult?.sending_line ? {
                                sending_line: sendResult.sending_line,
                                from: sendResult.from,
                                to: sendResult.to,
                            } : {}),
                            ...(deliveryChannel === 'email' && identity?.sourceId ? { email_provider_id: identity.sourceId } : {}),
                            body: recipientBody,
                            at: sentAt,
                            client_ref: sendResult?.clientRef || null,
                            provider_sid: sendResult?.providerSid || null,
                            provider_status: sendResult?.providerStatus || 'sent',
                            provider_status_updated_at: sentAt,
                            ...(contentSid ? { contentSid, contentVariables: templateVariables || {} } : {}),
                        };
                        const conversationEntry = {
                            role: 'staff',
                            content: recipientBody,
                            channel: deliveryChannel,
                            at: sentAt,
                            staffUserId: currentUserId,
                            staffName: sendResult?.staffName || staffNames[currentUserId] || 'Staff',
                            ...(contentSid ? { contentSid, contentVariables: templateVariables || {} } : {}),
                        };
                        const insertPayload = {
                            ...buildOutboundSessionInsert({ contact, option: recipient.option, sendingLine: sendResult?.sending_line || sendResult?.from || identity?.address }),
                            ...(deliveryChannel === 'email'
                                ? { email_provider_id: sendResult?.emailProviderId || identity?.sourceId || null }
                                : {}),
                            raw_message_log: [logEntry],
                            conversation_log: [conversationEntry],
                            last_outbound_at: sentAt,
                        };
                        const { data: newThread, error: sessionError } = await supabaseClient
                            .from('intake_sessions')
                            .insert(insertPayload)
                            .select(SESSION_COLUMNS)
                            .single();
                        if (sessionError || !newThread) throw new Error(sessionError?.message || 'Message sent, but its conversation could not be saved.');
                        session = newThread;
                        if (deliveryChannel === 'email' && newThread.email_provider_id) {
                            const folderResult = await ensureEmailConversationFolder(supabaseClient, {
                                orgId: newThread.org_id,
                                mailboxId: newThread.email_provider_id,
                                sessionId: newThread.id,
                                folderName: 'Sent',
                            });
                            if (folderResult.error) console.warn('Sent folder could not be recorded for the new email thread.', folderResult.error);
                        }
                        setSessions((previous) => [newThread, ...previous.filter((item) => item.id !== newThread.id)]);
                        setSelectedSessionId(newThread.id);
                        setComposeOpen(false);
                        if (newAccount && newContact) {
                            setAccounts((current) => [{ ...newAccount, primary_contact_id: newContact.id }, ...current.filter((row) => row.id !== newAccount.id)]);
                            setContacts((current) => [{ ...newContact, primary_account_id: newAccount.id }, ...current.filter((row) => row.id !== newContact.id)]);
                            createdLead = true;
                        }
                    }

                    if (campaignId && !campaignRecipientId) {
                        const { error: recipientError } = await supabaseClient
                            .from('comm_campaign_recipients')
                            .insert([{
                                org_id: resolvedOrgId,
                                campaign_id: campaignId,
                                contact_id: contact.id,
                                intake_session_id: session?.id || null,
                                destination: recipient.option?.address || selected?.option?.address || '',
                                status: sendResult?.providerStatus || 'sent',
                                provider_sid: sendResult?.providerSid || null,
                                sent_at: new Date().toISOString(),
                            }]);
                        if (recipientError) throw new Error(recipientError.message);
                    } else if (campaignRecipientId && session?.id) {
                        const { error: linkError } = await supabaseClient
                            .from('comm_campaign_recipients')
                            .update({ intake_session_id: session.id })
                            .eq('id', campaignRecipientId)
                            .eq('org_id', resolvedOrgId);
                        if (linkError) throw new Error(linkError.message || 'Could not link the campaign recipient to its conversation.');
                    }
                } catch (recipientError) {
                    if (campaignRecipientId) {
                        await supabaseClient.from('comm_campaign_recipients').update({ status: 'failed' }).eq('id', campaignRecipientId).eq('org_id', resolvedOrgId);
                    }
                    throw recipientError;
                }
                sent += 1;
                if (messageType === 'campaign') {
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                }
            }

            if (campaignId) {
                const { error: completionError } = await supabaseClient
                    .from('comm_campaigns')
                    .update({
                        status: 'completed',
                        completed_at: new Date().toISOString(),
                    })
                    .eq('id', campaignId);
                if (completionError) throw new Error(completionError.message || 'Messages were sent, but the campaign status could not be updated.');
            }
            loadSessions();
            refreshGateAfterSendRef.current?.();  // Comms gate fixes: a New message may have claimed the client's conversation
            return {
                sent,
                skipped,
                notice: createdLead
                    ? `Sent as ${staffNames[currentUserId] || 'Staff'} · the new number is saved to CRM as a lead`
                    : '',
            };
        } catch (sendError) {
            setComposeError(sendError.message || 'The message could not be sent.');
            if (campaignId) {
                await supabaseClient
                    .from('comm_campaigns')
                    .update({ status: 'completed_with_errors', completed_at: new Date().toISOString() })
                    .eq('id', campaignId);
            }
            return null;
        } finally {
            setStartingThread(false);
        }
    }, [contacts, currentUserId, defaultEntityId, loadSessions, resolvedOrgId, sessions, staffNames]);

    const handleThreadListResizeStart = useCallback((event) => {
        if (event.button !== 0 || event.isPrimary === false || resizePointerRef.current) return;
        event.preventDefault();
        resizePointerRef.current = { id: event.pointerId, target: event.currentTarget };
        event.currentTarget.setPointerCapture?.(event.pointerId);
        resizeStartXRef.current = event.clientX;
        resizeStartWidthRef.current = threadListWidth;
        setIsResizingThreadList(true);
    }, [threadListWidth]);

    const handleThreadListResizeKeyDown = useCallback((event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const containerWidth = resizeContainerRef.current?.getBoundingClientRect().width || 0;
        setThreadListContainerWidth(containerWidth);
        const { minWidth, maxWidth } = threadListWidthBounds(containerWidth);
        setThreadListWidth((current) => event.key === 'Home' ? minWidth : event.key === 'End' ? maxWidth
            : clampThreadListWidth(current + (event.key === 'ArrowRight' ? 10 : -10), containerWidth));
    }, []);

    useEffect(() => {
        const syncThreadListWidth = () => {
            const containerWidth = resizeContainerRef.current?.getBoundingClientRect().width || 0;
            if (!containerWidth) return;
            setThreadListContainerWidth(containerWidth);
            setThreadListWidth((current) => clampThreadListWidth(current, containerWidth));
        };

        syncThreadListWidth();
        window.addEventListener('resize', syncThreadListWidth);
        return () => window.removeEventListener('resize', syncThreadListWidth);
    }, []);

    useEffect(() => {
        if (!isResizingThreadList) return undefined;
        const previousUserSelect = document.body.style.userSelect;
        document.body.style.userSelect = 'none';

        const handlePointerMove = (event) => {
            if (event.pointerId !== resizePointerRef.current?.id) return;
            const delta = event.clientX - resizeStartXRef.current;
            const containerWidth = resizeContainerRef.current?.getBoundingClientRect().width || 0;
            setThreadListWidth(clampThreadListWidth(resizeStartWidthRef.current + delta, containerWidth));
        };
        const releasePointer = () => {
            const pointer = resizePointerRef.current;
            resizePointerRef.current = null;
            if (pointer?.target.hasPointerCapture?.(pointer.id)) pointer.target.releasePointerCapture(pointer.id);
        };
        const handlePointerEnd = (event) => {
            if (event.pointerId !== resizePointerRef.current?.id) return;
            releasePointer();
            setIsResizingThreadList(false);
        };

        document.addEventListener('pointermove', handlePointerMove);
        document.addEventListener('pointerup', handlePointerEnd);
        document.addEventListener('pointercancel', handlePointerEnd);
        return () => {
            document.body.style.userSelect = previousUserSelect;
            document.removeEventListener('pointermove', handlePointerMove);
            document.removeEventListener('pointerup', handlePointerEnd);
            document.removeEventListener('pointercancel', handlePointerEnd);
            releasePointer();
        };
    }, [isResizingThreadList]);

    // My Contacts supply a name for people the company does not name: the signed-in person's own, then ones colleagues shared with them
    // (row-level security returns nothing else). Skipped entirely when the platform/company switch is off.
    useEffect(() => {
        if (!currentUserId) { setMyContacts([]); return undefined; }
        let cancelled = false;
        (async () => {
            const status = await fetchPersonalContactsStatus(supabaseClient);
            if (!status.enabled) { if (!cancelled) setMyContacts([]); return; }
            try {
                const rows = await fetchNamingContacts(supabaseClient, currentUserId);
                if (!cancelled) setMyContacts(rows);
            } catch {
                if (!cancelled) setMyContacts([]);
            }
        })();
        return () => { cancelled = true; };
    }, [currentUserId]);

    const namingContacts = useMemo(
        () => (profileContact ? [...myContacts, profileContact] : myContacts),
        [myContacts, profileContact]
    );
    const accountsById = useMemo(() => new Map(accounts.map((account) => [account.id, account])), [accounts]);
    const contactSearchIndexes = useMemo(() => buildContactSearchIndexes(contacts), [contacts]);
    const { rows: scheduledRows, error: scheduledError, reload: reloadScheduled } = useScheduledMessages({ client: supabaseClient, enabled: Boolean(currentUserId) });
    const unnamedSessionIds = useMemo(() => storedSessions
        .filter((session) => !session.account_id && (session.contact_phone || session.contact_email))
        .map((session) => session.id).sort().join(','), [storedSessions]);
    useEffect(() => {
        if (!unnamedSessionIds || typeof supabaseClient.rpc !== 'function') return undefined;
        let stale = false;
        Promise.resolve(supabaseClient.rpc('crm_event_guest_labels', { p_sessions: unnamedSessionIds.split(',').slice(0, 500) })).then(({ data, error: labelError }) => {
            if (stale || labelError || !Array.isArray(data)) return;
            setEventGuestLabels(Object.fromEntries(data.map((row) => [row.session_id, { guest_name: row.guest_name, event_name: row.event_name }])));
        }).catch(() => {});
        return () => { stale = true; };
    }, [unnamedSessionIds]);

    const threads = useMemo(() => buildThreadList({
        sessions,
        scheduledRows,
        accountsById,
        contacts,
        contactSearchIndexes,
        resolveContactForSession: (session, account) => findContactForSession(session, contacts, account),
        resolveMyContactForSession: (session) => findMyContactForSession(session, namingContacts),
        threadSortOrder: communicationsSettings.threadSortOrder,
    }), [accountsById, communicationsSettings.threadSortOrder, contactSearchIndexes, contacts, namingContacts, sessions, scheduledRows]);
    const selectedThread = threads.find((thread) => thread.id === selectedSessionId) || null;
    // BL-177 Step 2e (6b): names for the people on the open group (CRM contacts); anyone else shows as their formatted number.
    const selectedParticipantNames = useMemo(
        () => participantNameMap(groupParticipants(selectedThread?.session?.contact_phone), [...contacts, ...namingContacts]),   // company names first (first match wins)
        [contacts, namingContacts, selectedThread?.session?.contact_phone],
    );
    const aiStatus = useMemo(() => describeAiAssistantStatus(aiAssistantSettings), [aiAssistantSettings]);
    // "All AI is paused by Val on Nov 4, 2:15 PM." while the company-wide emergency pause is on.
    const aiPauseLine = useMemo(() => describePause(aiAssistantSettings), [aiAssistantSettings]);
    const selectedAccount = selectedThread ? accountsById.get(selectedThread.session.account_id) || null : null;
    const selectedContact = selectedThread ? findContactForSession(selectedThread.session, contacts, selectedAccount) : null;
    const colleagueIds = useMemo(() => new Set(access.cards.filter((card) => card.session_id).map((card) => card.session_id)), [access.cards]);
    const inboxes = useMemo(() => splitByInbox(threads, { mineIds: access.mineIds, colleagueIds }), [access.mineIds, colleagueIds, threads]);
    const inboxCounts = useMemo(() => ({
        company: inboxes.company.filter((thread) => !thread.archivedAt).length,
        mine: inboxes.mine.filter((thread) => !thread.archivedAt).length,
    }), [inboxes]);
    const scopedThreads = inboxScope === 'mine' ? inboxes.mine : inboxes.company;
    const selectedCard = selectedSessionId ? access.cards.find((card) => card.session_id === selectedSessionId) || null : null;
    // Gated Group Communications (migration 284): who owns the open conversation and whether I may reply. Re-read whenever the
    // access picture changes (Take / Leave / Release / a refused send all announce it) or another conversation opens.
    const [gateState, setGateState] = useState({ sessionId: null, status: DEFAULT_GATE_STATUS, events: [] });
    const [gateNonce, setGateNonce] = useState(0);
    const reloadGate = useCallback(() => setGateNonce((value) => value + 1), []);
    refreshGateAfterSendRef.current = () => {
        try { access.reload?.(); } catch { /* the next poll catches up */ }
        reloadGate();
    };
    useEffect(() => {
        if (!selectedSessionId || !currentUserId) {
            setGateState({ sessionId: null, status: DEFAULT_GATE_STATUS, events: [] });
            return undefined;
        }
        let active = true;
        loadGateStatus(supabaseClient, selectedSessionId).then(async (status) => {
            const events = status.gated ? await loadGateTimeline(supabaseClient, selectedSessionId) : [];
            if (active) setGateState({ sessionId: selectedSessionId, status, events });
        });
        return () => { active = false; };
    }, [selectedSessionId, currentUserId, access.version, gateNonce]);
    const selectedGate = gateState.sessionId === selectedSessionId ? gateState.status : DEFAULT_GATE_STATUS;
    const selectedGateEvents = gateState.sessionId === selectedSessionId ? gateState.events : EMPTY_LIST;
    const gateReadOnly = Boolean(selectedGate.gated && selectedGate.owned && !selectedGate.iAmIn);
    const selectedAccessMode = !selectedSessionId ? 'company'
        : access.mineIds.has(selectedSessionId) ? 'mine'
            : (selectedCard || gateReadOnly) ? 'colleague' : 'company';
    // Opening a conversation (a link, a pop-up) from the other inbox switches the list to the inbox it is in.
    useEffect(() => {
        if (selectedAccessMode === 'mine') setInboxScope('mine');
        else if (selectedAccessMode === 'company' && selectedSessionId) setInboxScope('company');
    }, [selectedAccessMode, selectedSessionId]);
    // Who is in the open conversation (the People tab and the "Others are in this conversation" band), refreshed with every change.
    useEffect(() => {
        if (!selectedSessionId || selectedAccessMode !== 'mine') return undefined;
        let active = true;
        setPeople((current) => ({ ...current, sessionId: selectedSessionId, loading: current.sessionId !== selectedSessionId || current.rows.length === 0, error: '' }));
        listParticipants(supabaseClient, selectedSessionId)
            .then((rows) => { if (active) setPeople({ sessionId: selectedSessionId, rows, loading: false, error: '' }); })
            .catch((failure) => { if (active) setPeople({ sessionId: selectedSessionId, rows: [], loading: false, error: failure.message }); });
        return () => { active = false; };
    }, [selectedSessionId, selectedAccessMode, access.version]);
    const selectedAccess = selectedSessionId ? {
        mode: selectedAccessMode,
        people: people.sessionId === selectedSessionId ? people.rows : [],
        peopleLoading: people.loading,
        peopleError: people.error,
        busy: accessBusy,
        groupState: groupState(people.sessionId === selectedSessionId ? people.rows : [], pendingInviteFor(access.requests, selectedSessionId)),
        isHost: people.sessionId === selectedSessionId && isHostOf(people.rows),
        pendingInvite: pendingInviteFor(access.requests, selectedSessionId),
        onWithdraw: handleWithdraw,
        onCloseGroup: () => handleCloseGroup(selectedSessionId),
        readOnlyBy: (gateReadOnly ? selectedGate.ownerName : '') || selectedCard?.handler_names?.[0] || '',
        joinPending: selectedCard?.request_status === 'pending' || selectedGate.joinPending,
        gated: selectedGate.gated,
        ownerName: selectedGate.ownerName,
        iAmOwner: selectedGate.iAmOwner,
        canRelease: selectedGate.canRelease,
        onRelease: () => handleRelease(selectedSessionId),
        onTake: () => handleTake(selectedSessionId),
        onLeave: () => handleLeave(selectedSessionId),
        onPassTo: () => openAccessModal('pass', selectedSessionId),
        onInvite: (toUserId) => openAccessModal('invite', selectedSessionId, { toUserId: typeof toUserId === 'string' ? toUserId : '' }),
        onJoin: () => openAccessModal('join', selectedSessionId),
    } : null;
    // BL-177 PR C0: editing an EXISTING company contact is for company admins; adding one is open to everyone with access.
    const lockedEntityId = selectedAccount?.entity_id || selectedContact?.entity_id || null;
    const [canManageThreadContact, setCanManageThreadContact] = useState(null);
    useEffect(() => {
        let active = true;
        setCanManageThreadContact(null);
        if (!lockedEntityId) return () => { active = false; };
        supabaseClient.rpc('crm_can_manage_company_contacts', { p_entity: lockedEntityId }).then(({ data, error }) => {
            if (active) setCanManageThreadContact(!error && data === true);
        });
        return () => { active = false; };
    }, [lockedEntityId]);
    const threadNeedsReview = Boolean(selectedContact?.requires_human_review || selectedAccount?.requires_human_review);
    const contactLockedNotice = (selectedAccount || selectedContact) && canManageThreadContact === false && !threadNeedsReview
        ? 'Only company admins can edit an existing company contact. Ask an admin to update it.'
        : '';
    const contactNameOnlyNotice = (selectedAccount || selectedContact) && canManageThreadContact === false && threadNeedsReview
        ? 'This is a new contact. You can give it a name; an admin confirms the rest.'
        : '';
    const selectedReplyDraft = selectedSessionId ? replyDraftsBySession[selectedSessionId] : null;
    const handleSaveThreadContact = useCallback(async (form = {}) => {
        if (!selectedThread?.session) return false;
        setSavingThreadContact(true);
        setThreadContactError('');
        try {
            const session = selectedThread.session;
            const existingAccount = accountsById.get(session.account_id) || null;
            const existingContact = findContactForSession(session, contacts, existingAccount);
            const entityId = existingAccount?.entity_id || existingContact?.entity_id || defaultEntityId;
            if (!entityId) {
                setThreadContactError('Could not save contact: missing tenant entity.');
                return false;
            }
            // BL-177 PR C0: ANYONE with access can ADD a person or account; EDITING one that already exists is for company admins
            // (and CRM "manage" holders). The database enforces it; this asks first so the message is plain.
            const { data: canManage } = await supabaseClient.rpc('crm_can_manage_company_contacts', { p_entity: entityId });
            // The one exception (database-enforced): while a contact is still flagged "needs review" (every inbox-created one is), anyone
            // with access may NAME it. Names only: the account name/type and the person's name. An admin confirms the rest.
            const nameOnly = canManage !== true && (existingAccount?.id || existingContact?.id)
                && (existingContact?.requires_human_review || existingAccount?.requires_human_review);
            if ((existingAccount?.id || existingContact?.id) && canManage !== true && !nameOnly) {
                setThreadContactError('Only company admins can edit an existing company contact. Ask an admin to update it.');
                return false;
            }
            const accountPatch = buildAccountUpdate({
                accountName: form.accountName,
                accountKind: form.accountKind,
            });
            let account = existingAccount;
            if (account?.id) {
                const { error: accountUpdateError } = await supabaseClient
                    .from('accounts')
                    .update(accountPatch)
                    .eq('id', account.id);
                if (accountUpdateError) {
                    setThreadContactError(accountUpdateError.message || 'Could not update the account.');
                    return false;
                }
                account = { ...account, ...accountPatch };
            } else {
                const { data: insertedAccount, error: accountInsertError } = await supabaseClient
                    .from('accounts')
                    .insert([buildNewAccountInsert({
                        accountName: form.accountName,
                        entityId,
                        accountKind: form.accountKind,
                    })])
                    .select(ACCOUNT_COLUMNS)
                    .single();
                if (accountInsertError || !insertedAccount?.id) {
                    setThreadContactError(accountInsertError?.message || 'Could not create the account.');
                    return false;
                }
                account = insertedAccount;
            }

            // Phones and emails are labeled entries (one Primary each). Existing people: save the difference, never a one-value array.
            const formPhones = cleanEntries(form.phones?.length ? form.phones : [{ value: form.phone, label: 'mobile', is_primary: true }], 'phone');
            const formEmails = cleanEntries(form.emails?.length ? form.emails : [{ value: form.email, label: 'work', is_primary: true }], 'email');
            const contactPatch = buildContactUpdate({
                name: form.name,
                firstName: form.firstName, lastName: form.lastName,
                phone: primaryEntry(formPhones)?.value || '',
                email: primaryEntry(formEmails)?.value || '',
                primaryAccountId: account.id,
                includeChannels: false,
            });
            if (nameOnly) {
                // names only: nothing else may change until an admin confirms the contact
                Object.keys(contactPatch).forEach((key) => { if (!['name', 'first_name', 'last_name'].includes(key)) delete contactPatch[key]; });
            }
            let contact = existingContact;
            if (contact?.id) {
                const { error: contactUpdateError } = await withComms292Fallback((columns, patch) => supabaseClient.from('contacts').update(patch).eq('id', contact.id), '', contactPatch);
                if (contactUpdateError) {
                    setThreadContactError(contactUpdateError.message || 'Could not update the contact.');
                    return false;
                }
                if (!nameOnly) {
                    for (const [kind, rows] of [['phone', formPhones], ['email', formEmails]]) {
                        await saveContactMethods(supabaseClient, {
                            contactId: contact.id, kind, original: entriesOf(contact, kind), next: rows,
                        });
                    }
                }
                contact = { ...contact, ...contactPatch };
            } else {
                const contactInsert = buildNewContactInsert({
                    name: form.name,
                firstName: form.firstName, lastName: form.lastName,
                    phone: primaryEntry(formPhones)?.value || '',
                    email: primaryEntry(formEmails)?.value || '',
                    entityId,
                    primaryAccountId: account.id,
                });
                // channels go in as labeled entries below (a person adding a brand-new contact may insert entries, not relabel them later)
                delete contactInsert.emails;
                delete contactInsert.phones;
                const { data: insertedContact, error: contactInsertError } = await withComms292Fallback((columns, rows) => supabaseClient.from('contacts').insert(rows).select(columns).single(), CONTACT_COLUMNS, [contactInsert]);
                if (contactInsertError || !insertedContact?.id) {
                    setThreadContactError(contactInsertError?.message || 'Could not create the contact.');
                    return false;
                }
                contact = insertedContact;
                for (const [kind, rows] of [['phone', formPhones], ['email', formEmails]]) {
                    // the first one inserted becomes the Primary, so the Primary goes first
                    for (const row of [...rows.filter(r => r.is_primary), ...rows.filter(r => !r.is_primary)]) {
                        const { error: methodError } = await supabaseClient.from('contact_methods').insert([{
                            contact_id: contact.id, kind, label: row.label, value: row.value, is_primary: row.is_primary,
                        }]);
                        if (methodError) throw methodError;
                        // 1.12.212-beta: a newly added phone is checked once (mobile / landline / VoIP); never blocks the save.
                        if (kind === 'phone') checkPhoneLine(supabaseClient, capabilityPhone(row.value));
                    }
                }
            }
            try {
                // pick up the database's own view (the flat phones/emails mirror) so the conversation list matches on every number
                const { data: refreshed } = await withComms292Fallback(columns => supabaseClient.from('contacts').select(columns).eq('id', contact.id).maybeSingle(), CONTACT_COLUMNS);
                if (refreshed?.id) contact = refreshed;
            } catch (refreshError) {
                // best effort: the save itself already succeeded
            }

            const sessionPatch = {
                account_id: account.id,
                // the thread keeps its own address when it is still one of the person's; otherwise it follows the Primary
                contact_phone: (formPhones.some(r => r.value === session.contact_phone) ? session.contact_phone : primaryEntry(formPhones)?.value) || null,
                contact_email: (formEmails.some(r => r.value.toLowerCase() === (session.contact_email || '').toLowerCase()) ? session.contact_email : primaryEntry(formEmails)?.value) || null,
            };
            const { error: sessionUpdateError } = await supabaseClient
                .from('intake_sessions')
                .update(sessionPatch)
                .eq('id', session.id);
            if (sessionUpdateError) {
                setThreadContactError(sessionUpdateError.message || 'Could not update the thread contact.');
                return false;
            }

            setAccounts((previous) => [account, ...previous.filter((row) => row.id !== account.id)]);
            setContacts((previous) => [contact, ...previous.filter((row) => row.id !== contact.id)]);
            setSessions((previous) => previous.map((row) => (row.id === session.id ? { ...row, ...sessionPatch } : row)));
            return true;
        } catch (saveError) {
            setThreadContactError(saveError.message || 'Could not save the contact.');
            return false;
        } finally {
            setSavingThreadContact(false);
        }
    }, [accountsById, contacts, defaultEntityId, selectedThread]);

    const handleWhatsAppFlagChange = useCallback(async (phone, index, whatsapp) => {
        const selectedContact = selectedThread
            ? findContactForSession(selectedThread.session, contacts, accountsById.get(selectedThread.session.account_id))
            : null;
        if (!selectedContact?.id) return;
        const phones = (Array.isArray(selectedContact.phones) ? selectedContact.phones : []).map((entry, entryIndex) => {
            if (entryIndex !== index) return entry;
            if (typeof entry === 'string') return { number: entry, whatsapp };
            const next = { ...entry, whatsapp };
            if (!getContactPhoneNumber(next)) next.number = getContactPhoneNumber(phone);
            return next;
        });
        const { error: updateError } = await supabaseClient
            .from('contacts')
            .update({ phones })
            .eq('id', selectedContact.id);
        if (updateError) {
            setThreadContactError(updateError.message || 'Could not update the WhatsApp phone setting.');
            return;
        }
        setContacts((current) => current.map((contact) => (
            contact.id === selectedContact.id ? { ...contact, phones } : contact
        )));
        setThreadContactError('');
    }, [accountsById, contacts, selectedThread]);

    const handleDeleteThread = useCallback(async () => {
        if (!selectedThread?.id) return;
        setDeletingThread(true);
        setThreadContactError('');
        const threadId = selectedThread.id;
        try {
            const { error: deleteError } = await supabaseClient
                .from('intake_sessions')
                .delete()
                .eq('id', threadId);
            if (deleteError) {
                setThreadContactError(deleteError.message || 'Could not delete the conversation.');
                return;
            }
            setSessions((previous) => previous.filter((session) => session.id !== threadId));
            setReplyDraftsBySession((previous) => {
                if (!previous[threadId]) return previous;
                const next = { ...previous };
                delete next[threadId];
                return next;
            });
            setSelectedSessionId(null);
        } catch (deleteError) {
            setThreadContactError(deleteError.message || 'Could not delete the conversation.');
        } finally {
            setDeletingThread(false);
        }
    }, [selectedThread]);

    const handleToggleArchive = useCallback(async (sessionId, shouldArchive) => {
        if (!sessionId) return;
        const patch = shouldArchive
            ? { archived_at: new Date().toISOString(), archived_by: currentUserId || null }
            : { archived_at: null, archived_by: null };
        const { error: archiveError } = await supabaseClient
            .from('intake_sessions')
            .update(patch)
            .eq('id', sessionId)
            .eq('org_id', resolvedOrgId);
        if (archiveError) {
            setError(archiveError.message || `Could not ${shouldArchive ? 'archive' : 'unarchive'} this thread.`);
            return;
        }
        setSessions((previous) => previous.map((session) => (session.id === sessionId ? { ...session, ...patch } : session)));
        if (shouldArchive && selectedSessionId === sessionId) {
            setSelectedSessionId(null);
        }
    }, [currentUserId, resolvedOrgId, selectedSessionId]);

    // BL-177 Step 3: schedule the message composed in "+ New message" for ONE person (their existing conversation, or a new one) or ONE
    // group. The conversation is reserved now; the scheduler sends the message later through the normal path.
    const handleScheduleMessage = useCallback(async ({
        recipients = [], identity, channel, subject, body, mediaUrl, template, templateVariables, templateBody, groupPhones = null, messageType, at, cancelOnReply = false,
        sendingIdentityExplicitlyChosen = false,
    } = {}) => {
        if (!currentUserId || !resolvedOrgId) throw new Error('Could not schedule: missing staff or organization scope.');
        const isGroup = messageType === 'group' && Array.isArray(groupPhones) && groupPhones.length >= 2;
        const deliveryChannel = channel === 'sms' && mediaUrl ? 'mms' : channel;
        const contentSid = channel === 'whatsapp' && messageType !== 'general'
            ? (template?.contentSid || template?.sid || '')
            : '';
        const common = {
            orgId: resolvedOrgId, channel: deliveryChannel, body, subject: channel === 'email' ? subject : '', mediaUrl, contentSid,
            contentVariables: contentSid ? templateVariables : null, templateBody: contentSid ? templateBody : '',
            sendingIdentityId: identity?.sourceId || null, sendingIdentityExplicitlyChosen, scheduledFor: at, cancelOnReply,
        };
        let scheduled;
        if (isGroup) {
            scheduled = await scheduleStaffReply({ ...common, session: null, groupAddresses: groupPhones, client: supabaseClient });
        } else {
            const recipient = recipients[0];
            const session = recipient?.contact ? findActiveThreadForContact(recipient.contact, sessions.filter(s => s.channel === channel || (channel === 'sms' && s.channel === 'mms'))) : null;
            scheduled = await scheduleStaffReply({ ...common, session, recipientAddress: session ? '' : (recipient?.option?.address || ''), client: supabaseClient });
        }
        await loadSessions();
        reloadScheduled();
        if (scheduled?.session_id) {
            setSelectedSessionId(scheduled.session_id);
            setComposeOpen(false);
        }
        return { notice: `Scheduled for ${formatScheduledWhen(scheduled?.scheduled_for || at, scheduled?.company_time_zone || 'America/New_York')}.` };
    }, [currentUserId, resolvedOrgId, sessions, loadSessions, reloadScheduled]);

    // BL-177 Step 3: the Scheduled view (everything the signed-in person may see: their own, or the company's for an admin).

    // The Scheduled view names who scheduled each message, so names for those people are looked up too.
    useEffect(() => {
        const ids = [...new Set(scheduledRows.map((row) => row.created_by).filter((id) => id && !staffNames[id]))];
        if (!ids.length) return;
        Promise.resolve(supabaseClient.rpc('bl177_staff_display_names', { p_user_ids: ids })).then(({ data }) => {
            if (!Array.isArray(data)) return;
            const found = Object.fromEntries(data.filter((row) => row?.display_name).map((row) => [row.user_id, row.display_name]));
            if (Object.keys(found).length) setStaffNames((current) => ({ ...current, ...found }));
        }).catch(() => {});
    }, [scheduledRows, staffNames]);
    const handleCancelScheduled = useCallback(async (row) => {
        await cancelScheduledMessage(row.id, supabaseClient);
        await loadSessions();
        reloadScheduled();
    }, [loadSessions, reloadScheduled]);

    const composePanel = composeOpen ? (
        <ComposeNewMessagePanel autoSelectMessageType={communicationsSettings.autoSelectMessageType}
            contacts={contacts}
            sessions={sessions}
            search={contactSearch}
            onSearchChange={setContactSearch}
            onCancel={() => setComposeOpen(false)}
            onOpenThread={(session) => handleOpenThread(session?.id)}
            defaultEntityId={defaultEntityId}
            orgId={resolvedOrgId}
            starting={startingThread}
            error={composeError || contactError || ''}
            allowSubjectLine={communicationsSettings.allowSubjectLine}
            theme={theme}
            userId={currentUserId}
            platformDoorContext={platformDoorContext}
            headerBrandEntityId={headerBrandEntityId}
            sendingIdentities={sendingIdentities}
            templates={templates}
            templatesLoading={templatesLoading}
            brandNameForEntity={(entityId) => brandNameForEntity(entities, entityId)}
            onSendMessage={handleSendMessage}
            scheduleSendEnabled
            onScheduleMessage={handleScheduleMessage}
            initialBody={composeRestore?.body || ''}
            initialSubject={composeRestore?.subject || ''}
        />
    ) : null;

    // Comms final pass (item 1): pull to refresh on touch devices reloads in place with the same loaders the timers use.
    const handlePullRefreshList = useCallback(async () => {
        await Promise.all([loadSessions(), access.reload()]);
    }, [access, loadSessions]);
    const handlePullRefreshConversation = useCallback(async () => {
        await Promise.all([loadSessions(), access.reload()]);
        reloadGate();
    }, [access, loadSessions, reloadGate]);

    // Comms final pass (item 4): what every template picker may fill on its own: the sender's first name, the open conversation's
    // company (its account's brand, else the company's sending brand), the company time zone and the greeting cut-offs.
    const [groupSettings, setGroupSettings] = useState(null);
    useEffect(() => {
        let active = true;
        loadGroupSettings(supabaseClient, resolvedOrgId).then((row) => { if (active) setGroupSettings(row); });
        return () => { active = false; };
    }, [resolvedOrgId]);
    const conversationBrandName = useMemo(() => {
        const byId = new Map(entities.map((entity) => [entity.id, entity]));
        let entity = byId.get(selectedAccount?.entity_id || selectedContact?.entity_id || '') || null;
        for (let hops = 0; entity && hops < 8; hops += 1) {
            if (entity.brand_name) return entity.brand_name;
            entity = byId.get(entity.parent_entity_id) || null;
        }
        return sendingIdentities.find((identity) => identity.entityName)?.entityName || '';
    }, [entities, selectedAccount?.entity_id, selectedContact?.entity_id, sendingIdentities]);
    useEffect(() => {
        setTemplateRuntimeDefaults({
            staffName: staffNames[currentUserId] || '',
            // Comms gate fixes: no page-wide "Company name". It used to be the brand of whichever conversation was opened last, so
            // a New message or a campaign could carry another brand's name. Each composer now passes the brand of the
            // conversation / sender it is actually sending for (templateCompanyName below); with none, staff are asked to type it.
            companyName: '',
            timezone: aiAssistantSettings?.timezone || '',
            greetingCutoffs: groupSettings
                ? { afternoon: groupSettings.greetingAfternoonHour, evening: groupSettings.greetingEveningHour }
                : undefined,
        });
    }, [aiAssistantSettings?.timezone, currentUserId, groupSettings, staffNames]);

    const panelCls = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';
    const headerButtonCls = 'ia-btn3d';
    // BL-177 Step 2n: on phones the list and the conversation are separate screens (styles/step2n.css shows one of them);
    // a conversation (or the New message form) open = the "thread" screen, otherwise the "list" screen.
    const commsScreen = composeOpen || selectedThread ? 'thread' : 'list';
    const handleBackToList = () => {
        if (composeOpen) setComposeOpen(false);
        setSelectedSessionId(null);
    };

    return (
        <EnterKeyPreferenceContext.Provider value={enterKeyMode}>
        {/* ia-hover-pulse-scope: every button in the workspace gets the shared turquoise hover pulse. */}
        <div
            ref={commsRootRef}
            className="ia-d2 ia-comms ia-hover-pulse-scope h-full min-w-0 flex flex-col"
            style={{ ...buildCommunicationsCssVars(communicationsSettings), ...STEP2D_COMMS_VARS }}
            data-theme={theme}
            data-comms-screen={commsScreen}
            data-comms-viewport={commsViewport}
        >
            <div className="ia-comms-pagehead relative mb-5 flex min-w-0 flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold text-white">Communications</h1>
                    <p className="text-sm mt-1 text-white">Client conversations across SMS, WhatsApp, email{teamsEnabled ? ' and Teams' : ''}.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {['email', 'teams', 'teams-client'].includes(workspaceTab) && (
                        <button
                            type="button"
                            onClick={() => setWorkspaceTab('conversations')}
                            className={headerButtonCls}
                            aria-label="Back to Conversations"
                        >
                            ← Conversations
                        </button>
                    )}
                    <AppLink
                        to={AI_INTEGRATION_PATH}
                        aria-label="AI status, open Business schedule"
                        title="Open Business schedule to change the AI"
                        data-ai-status-link="true"
                        className="rounded-full transition hover:brightness-110 active:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5EEAD4]"
                    >
                        <StatusChip tone={aiStatus.tone}>
                            {aiStatus.label}
                        </StatusChip>
                    </AppLink>
                    <button
                        type="button"
                        onClick={() => setSettingsOpen(true)}
                        className={headerButtonCls}
                        aria-label="Open Communications settings"
                        title="Communications settings"
                    >
                        ⚙
                    </button>
                </div>
            </div>
            {aiPauseLine && (
                <div role="status" className={`ia-ai-pause-banner mb-3 ${dark ? 'is-dark' : ''}`} data-testid="comms-ai-pause-banner">{aiPauseLine}</div>
            )}
            {accessError && (
                <div role="alert" className="mb-3 text-sm text-[#FCA5A5]" data-access-error="true">{accessError}</div>
            )}
            {openNotice && (
                <div role="alert" className="ia-open-notice mb-3 flex flex-wrap items-center gap-2 text-sm" data-open-notice="true">
                    <span>{openNotice}</span>
                    <button type="button" className="ia-btn3d ia-comms-pill" onClick={() => { setOpenNotice(''); setSelectedSessionId(null); }}>Dismiss</button>
                </div>
            )}
            {flagError && (
                <div role="alert" className="mb-3 text-sm text-[#FCA5A5]">Could not update the flag: {flagError}</div>
            )}
            {teamsEnabled && ['teams', 'teams-client', 'email'].includes(workspaceTab) && <nav aria-label="Teams workspaces" className="flex flex-wrap gap-2 mb-3">
                <button type="button" className={headerButtonCls} onClick={() => setWorkspaceTab('email')}>Email inbox</button>
                <button type="button" className={headerButtonCls} onClick={() => setWorkspaceTab('teams')}>Team chat</button>
                <button type="button" className={headerButtonCls} onClick={() => setWorkspaceTab('teams-client')}>Conversations · Teams · External</button>
            </nav>}
            {['teams', 'teams-client'].includes(workspaceTab) ? (
                teamsEnabled ? <TeamsTextMirror key={`${workspaceTab}-${teamsThreadId || ''}`} userId={currentUserId} initialThreadId={teamsThreadId} mode={workspaceTab === 'teams-client' ? 'client' : 'team'} /> : null
            ) : workspaceTab === 'email' ? (
                <EmailMailboxWorkspace
                    orgId={resolvedOrgId}
                    sessions={sessions}
                    contacts={contacts}
                    accounts={accounts}
                    currentUserId={currentUserId}
                    theme={theme}
                    onOpenConversation={handleEmailReply}
                />
            ) : <div ref={resizeContainerRef} className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 md:flex-row md:gap-0" style={{ '--ia-comms-list-w': `${threadListWidth}px` }}>
                {/* Phones stack the list above the conversation; from md up the list keeps its resizable width. */}
                <div className={`ia-comms-list w-full min-w-0 max-h-[60vh] shrink-0 rounded-xl border md:max-h-none ${panelCls}`} data-thread-list-pane="true">
                    <CommunicationsThreadList
                        threads={scopedThreads}
                        otherInboxThreads={inboxScope === 'mine' ? inboxes.company : inboxes.mine}
                        suggestionContacts={contacts}
                        suggestionAccounts={accounts}
                        selectedId={selectedSessionId}
                        mentionedSessionIds={mentionedSessionIds}
                        onSelect={handleOpenThread}
                        search={search}
                        onSearchChange={setSearch}
                        loading={loading}
                        error={error}
                        campaigns={campaigns}
                        campaignError={campaignError}
                        onSetFlag={handleSetFlag}
                        onNewMessage={handleOpenNewMessage}
                        onAddToCrm={handleAddToCrm}
                        onOpenCampaignThread={handleOpenThread}
                        inboxScope={inboxScope}
                        onInboxScopeChange={setInboxScope}
                        inboxCounts={inboxCounts}
                        colleagueSection={<>
                            {teamsEnabled && <TeamsTextMirror mode="client-list" userId={currentUserId} onOpenClient={row => {
                                setTeamsThreadId(row.id); setWorkspaceTab('teams-client');
                            }} />}
                            {inboxScope === 'company' ? (
                            <ColleagueConversations
                                cards={access.cards}
                                busyId={accessBusy ? 'busy' : ''}
                                onView={handleOpenThread}
                                onJoin={(card) => openAccessModal('join', card.session_id)}
                                onWithdraw={handleWithdraw}
                            />
                        ) : null}</>}
                        groupThreadsByAccount={communicationsSettings.groupThreadsByAccount}
                        groupThreadsByStatus={communicationsSettings.groupThreadsByStatus}
                        statusCategoryOrder={communicationsSettings.statusCategoryOrder}
                        pinnedThreadPosition={communicationsSettings.pinnedThreadPosition}
                        theme={theme}
                        userId={currentUserId}
                        channelActiveHours={communicationsSettings.channelActiveHours}
                        channelCoolingHours={communicationsSettings.channelCoolingHours}
                        handoffNudgeMinutes={communicationsSettings.handoffNudgeMinutes}
                        onToggleArchive={handleToggleArchive}
                        previewLines={previewLines}
                        workspaceTab={workspaceTab}
                        onWorkspaceTabChange={setWorkspaceTab}
                        teamsEnabled={teamsEnabled}
                        scheduledRows={scheduledRows}
                        scheduledError={scheduledError}
                        staffNames={staffNames}
                        isAdmin={isCompanyAdmin}
                        onOpenScheduled={(row) => handleOpenThread(row.session_id)}
                        onCancelScheduled={handleCancelScheduled}
                        voicemailStateByKey={voicemailStateByKey}
                        onUpdateVoicemailState={handleUpdateVoicemailState}
                        voicemailStateError={voicemailStateError}
                        voicemailStateLoading={voicemailStateLoading}
                        onPullRefresh={handlePullRefreshList}
                    />
                </div>
                <button
                    type="button"
                    aria-label="Resize conversation list"
                    role="separator"
                    aria-orientation="vertical"
                    aria-valuemin={resizeBounds.minWidth}
                    aria-valuemax={resizeBounds.maxWidth}
                    aria-valuenow={threadListWidth}
                    aria-valuetext={`${threadListWidth} pixels`}
                    onPointerDown={handleThreadListResizeStart}
                    onLostPointerCapture={() => setIsResizingThreadList(false)}
                    onKeyDown={handleThreadListResizeKeyDown}
                    style={{ touchAction: 'none' }}
                    onDragStart={(event) => event.preventDefault()}
                    className="ia-hover-pulse-off group/resize hidden w-2 shrink-0 cursor-col-resize items-stretch justify-center bg-transparent md:flex"
                >
                    <span className={`my-2 w-px rounded-full ${dark ? 'bg-gray-700 group-hover/resize:bg-gray-500' : 'bg-gray-200 group-hover/resize:bg-gray-400'} ${isResizingThreadList ? (dark ? 'bg-gray-500' : 'bg-gray-400') : ''}`} />
                </button>
                <div className="ia-comms-thread-pane min-h-[70vh] flex-1 min-w-0 md:min-h-0">
                    {composePanel || (selectedThread ? (
                        <IntakeSessionDetailCard reservationAction={selectedContact && selectedAccount ? <StaffReservation client={supabaseClient} accounts={accounts} accountId={selectedAccount.id} /> : null} groupEmailsBySubject={communicationsSettings.groupEmailsBySubject} showMessageCount={communicationsSettings.showMessageCount} autoSelectMessageType={communicationsSettings.autoSelectMessageType}
                            reconnectionMinutes={communicationsSettings.reconnectionMinutes}
                            intakeSession={selectedThread.session}
                            account={selectedAccount}
                            contact={selectedContact}
                            myContact={findMyContactForSession(selectedThread.session, namingContacts)}
                            allSessions={sessions}
                            staffNames={staffNames}
                            participantNames={selectedParticipantNames}
                            access={selectedAccess}
                            onClose={() => setSelectedSessionId(null)}
                            onBack={handleBackToList}
                            onSaveContact={handleSaveThreadContact}
                            openContactEditorRequest={contactEditorRequest}
                            onWhatsAppFlagChange={handleWhatsAppFlagChange}
                            onDeleteThread={handleDeleteThread}
                            onReplySent={handleReplySent}
                            onMarkInternalNotesRead={handleMarkInternalNotesRead}
                            onAddInternalNote={handleAddInternalNote}
                            mentionNoteFocus={noteFocus && noteFocus.sessionId === selectedSessionId ? noteFocus : null}
                            onSetFlag={(change) => handleSetFlag(selectedThread.id, change)}
                            currentUserId={currentUserId}
                            platformDoorContext={platformDoorContext}
                            headerBrandEntityId={headerBrandEntityId}
                            aiPauseSettings={aiAssistantSettings}
                            initialReplyBody={selectedReplyDraft?.body || ''}
                            initialReplySubject={selectedReplyDraft?.subject || ''}
                            savingContact={savingThreadContact}
                            deletingThread={deletingThread}
                            contactError={threadContactError}
                            contactLockedNotice={contactLockedNotice}
                            contactNameOnlyNotice={contactNameOnlyNotice}
                            onReservationChanged={loadSessions}
                            onTestContactReset={() => { setSelectedSessionId(null); loadSessions(); }}
                            onHandBackToAi={() => handleHandBackToAi(selectedThread.id)}
                            onToggleArchive={() => {
                                const latestSession = sessions.find((session) => session.id === selectedThread.id);
                                handleToggleArchive(selectedThread.id, !latestSession?.archived_at);
                            }}
                            theme={theme}
                            sendingIdentities={sendingIdentities}
                            companyName={sendingIdentities.find((identity) => identity.entityName)?.entityName || ''}
                            showCompanyName={new Set(entities
                                .filter((entity) => entity?.status === 'active' && /brand/i.test(entity?.display_label || ''))
                                .map((entity) => entity?.brand_name || entity?.name)
                                .filter(Boolean)).size > 1}
                            templates={templates}
                            templateCompanyName={conversationBrandName}
                            conversationIdleMinutes={aiAssistantSettings.conversation_idle_minutes}
                            voicemailStateByKey={voicemailStateByKey}
                            onUpdateVoicemailState={handleUpdateVoicemailState}
                            voicemailStateError={voicemailStateError}
                            onPullRefresh={handlePullRefreshConversation}
                            gateEvents={selectedGateEvents}
                        />
                    ) : (
                        <div className={`h-full rounded-xl border flex flex-col items-center justify-center text-center ${panelCls}`}>
                            <div className="text-4xl mb-2">💬</div>
                            <div className="font-medium text-white">No conversation selected</div>
                            <div className="text-sm text-white">Pick a conversation on the left to read it.</div>
                        </div>
                    ))}
                </div>
            </div>}
            {accessModal && (
                <ConversationAccessModal
                    title={accessModal.kind === 'join' ? 'Ask to join this conversation' : accessModal.kind === 'pass' ? 'Pass this conversation to a colleague' : 'Invite a colleague'}
                    intro={accessModal.kind === 'join'
                        ? 'They see your note and choose Allow or Decline. You are not let in until they allow it.'
                        : 'Your colleague sees your note and chooses Allow or Decline; nothing changes until then.'}
                    noteLabel={accessModal.kind === 'join' ? 'Why do you need to join?' : 'Note for your colleague'}
                    submitLabel={accessModal.kind === 'join' ? 'Send request' : accessModal.kind === 'pass' ? 'Pass it on' : 'Send invite'}
                    people={accessModal.kind === 'join' ? null : invitable.rows || []}
                    peopleLoading={accessModal.kind !== 'join' && invitable.loading}
                    peopleLabel={accessModal.kind === 'pass' ? 'Pass to' : 'Invite'}
                    busy={accessBusy}
                    error={accessModalError}
                    onSubmit={submitAccessModal}
                    onClose={() => setAccessModal(null)}
                    initialToUserId={accessModal.toUserId || ''}
                />
            )}
            {settingsOpen && (
                <CommunicationsSettingsModal
                    sendingIdentities={sendingIdentities}
                    settings={communicationsSettings}
                    onClose={() => setSettingsOpen(false)}
                    onSave={handleSaveSettings}
                    saving={settingsSaving}
                    error={settingsError}
                    theme={theme}
                    userId={currentUserId}
                    previewLines={previewLines}
                    onPreviewLinesChange={handlePreviewLinesChange}
                    canChangeNumberCheckLimit={isCompanyAdmin}
                    groupSettingsClient={supabaseClient}
                    orgId={resolvedOrgId}
                    canChangeGroupSettings={isCompanyAdmin}
                    onGroupSettingsSaved={(next) => { setGroupSettings(next); reloadGate(); }}
                />
            )}
        </div>
        </EnterKeyPreferenceContext.Provider>
    );
}

export default CommunicationsView;
